import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Av, Dot, Textarea } from '../ui.jsx';
import { Safe } from '../widgets/ErrorBoundary.jsx';

export function Agency({ user, toast }) {
  // Defense in depth — sidebar already hides this tab for non-super-admins,
  // but if a client somehow lands here (deep link), show a polite block.
  if (user?.role !== 'super_admin') {
    return <div style={{ padding:40, textAlign:'center', color:G.textMuted }}>
      <div style={{ ...styles.card, maxWidth:480, margin:'0 auto', padding:30 }}>
        <h2 style={{ margin:'0 0 10px' }}>Agency console</h2>
        <p style={{ margin:0 }}>This area is for platform admins. If you believe you should have access, ask your TradeOps contact.</p>
      </div>
    </div>;
  }
  const [tab, setTab] = useState('accounts');
  const TABS = [
    { id:'accounts',  label:'🏢 Sub-Accounts' },
    { id:'margin',    label:'💰 Margin' },
    { id:'compare',   label:'🥇 Compare' },
    { id:'marketplace', label:'🔀 Marketplace' },
    { id:'consent',   label:'✅ Consent Audit' },
    { id:'coaching',  label:'📈 Coaching' },
    { id:'kpis',      label:'📊 KPIs' },
    { id:'snapshots', label:'📦 Snapshots' },
    { id:'push',      label:'🚀 Multi-Push' },
    { id:'ab',        label:'🧪 A/B' },
    { id:'numbers',   label:'☎️ Numbers' },
    { id:'network',   label:'🧠 Winners' },
    { id:'backups',   label:'💾 Backups' },
    { id:'a2p',       label:'📲 A2P 10DLC' },
  ];
  return <div style={{ padding:'24px 24px 24px 24px', maxWidth:'100%', overflowX:'hidden' }}>
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18, gap:12, flexWrap:'wrap' }}>
      <div style={{ minWidth:0, flex:1 }}>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Agency Console</h1>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>Multi-tenant management. Switch into any sub-account or push snapshots across many at once.</div>
      </div>
      <Badge c={G.purple} bg={G.purpleDim}>Super Admin · {user.email}</Badge>
    </div>
    {/* Horizontal-scroll tab strip — keeps all tabs on one row on every viewport.
        Native scrollbar hidden via :webkit-scrollbar for cleaner look; touch
        swipe handles overflow on mobile. */}
    <div style={{ marginBottom:18, background:G.input, padding:4, borderRadius:10, overflowX:'auto', WebkitOverflowScrolling:'touch', scrollbarWidth:'none' }}>
      <div style={{ display:'flex', gap:6, flexWrap:'nowrap', width:'max-content' }}>
        {TABS.map(t => (
          <Btn key={t.id} sz="sm" v={tab===t.id?'primary':'ghost'} onClick={()=>setTab(t.id)} style={{ whiteSpace:'nowrap', flexShrink:0 }}>
            {t.label}
          </Btn>
        ))}
      </div>
    </div>
    {tab === 'kpis' && <KpisTab toast={toast}/>}
    {tab === 'margin' && <MarginCoachTab toast={toast}/>}
    {tab === 'accounts' && <AccountsTab toast={toast} user={user}/>}
    {tab === 'snapshots' && <SnapshotsTab toast={toast}/>}
    {tab === 'push' && <PushTab toast={toast}/>}
    {tab === 'ab' && <AbTab toast={toast}/>}
    {tab === 'numbers' && <NumbersTab toast={toast}/>}
    {tab === 'network' && <AgencyNetworkWinners toast={toast}/>}
    {tab === 'coaching' && <CoachingReportsTab toast={toast}/>}
    {tab === 'backups' && <AgencyBackups toast={toast}/>}
    {tab === 'a2p' && <A2PTab toast={toast}/>}
    {tab === 'compare' && <CompareCohortTab toast={toast}/>}
    {tab === 'marketplace' && <MarketplaceDashboardTab toast={toast}/>}
    {tab === 'consent' && <ConsentAuditTab toast={toast}/>}
  </div>;
}

// ─── Compare (like-for-like cohort winners) ────────────────────────────────
// Group sub-accounts by vertical + plan-tier and rank them within cohort by
// conversion metrics. This is the "which HVAC account converts best" view
// so agency can push the winner's playbook to the rest of the cohort.
function CompareCohortTab({ toast }) {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(30);
  React.useEffect(() => {
    setData(null);
    api(`/api/agency/cohort-compare?days=${days}`).then(setData).catch(e=>toast(e.message,'error'));
  }, [days]);
  if (!data) return <Spinner sz={32}/>;
  const cohorts = data.cohorts || [];
  return <div>
    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
      <div style={{ fontSize:20, fontWeight:800 }}>🥇 Cohort Compare</div>
      <div style={{ fontSize:12, color:G.textMuted, flex:1 }}>Like-for-like sub-account performance grouped by vertical + plan. Push winners' playbook to the rest of the cohort.</div>
      <Select value={String(days)} onChange={e=>setDays(Number(e.target.value))} style={{ width:120 }}>
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
      </Select>
      <Btn v="primary" onClick={async()=>{
        try {
          const p = await api('/api/agency/auto-adopt/preview');
          if (!p.proposals.length) return alert('No qualifying proposals right now — a cohort winner needs ≥30 leads + ≥5 booked + a 20% score lead over #2 before its playbook gets pushed.');
          const summary = p.proposals.map(x =>
            `• ${x.cohortLabel}: ${x.winner.name} → ${x.laggers.length} lagger${x.laggers.length===1?'':'s'} (${x.laggers.map(l=>l.name).join(', ')})`
          ).join('\n');
          const auto = p.autoEnabled ? 'AUTO-PUSH is ON — proposals will apply immediately.' : 'AUTO-PUSH is OFF — clicking Run only creates proposals for review.';
          if (!confirm(`Would push winner workflows to laggers in these cohorts:\n\n${summary}\n\n${auto}\n\nProceed?`)) return;
          const r = await api('/api/agency/auto-adopt/run', { method:'POST' });
          const pushed = r.proposals.filter(x => x.autoPushed);
          toast(`✓ ${pushed.length} auto-pushed · ${r.proposals.length - pushed.length} pending review`);
        } catch (e) { toast(e.message, 'error'); }
      }}>🥇 Push Winner Playbooks</Btn>
    </div>
    {!cohorts.length && <div style={{ ...styles.card, padding:32, textAlign:'center', color:G.textMuted }}>
      No cohorts to compare — need at least 2 active sub-accounts sharing a vertical.
    </div>}
    {cohorts.map((c, i) => (
      <div key={i} style={{ ...styles.card, padding:18, marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div>
            <strong style={{ fontSize:16 }}>{c.label}</strong>
            <div style={{ fontSize:12, color:G.textMuted }}>{c.accounts.length} sub-accounts</div>
          </div>
          {c.accounts[0] && <Badge c={G.green} bg={G.green+'22'}>Winner: {c.accounts[0].name}</Badge>}
        </div>
        <table style={{ width:'100%', fontSize:13 }}>
          <thead><tr style={{ color:G.textMuted, fontSize:11, textTransform:'uppercase' }}>
            <th style={{ textAlign:'left', padding:'6px 8px' }}>#</th>
            <th style={{ textAlign:'left', padding:'6px 8px' }}>Account</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Leads</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Booked</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Close %</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Avg Response</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Revenue</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Score</th>
          </tr></thead>
          <tbody>
            {c.accounts.map((a, idx) => (
              <tr key={a.id} style={{ borderTop:`1px solid ${G.border}`, background: idx===0 ? G.green+'11' : 'transparent' }}>
                <td style={{ padding:'6px 8px', fontWeight:700 }}>{idx===0?'🥇':idx===1?'🥈':idx===2?'🥉':idx+1}</td>
                <td style={{ padding:'6px 8px', fontWeight:600 }}>{a.name}</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{a.leads}</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{a.booked}</td>
                <td style={{ padding:'6px 8px', textAlign:'right', color: a.closePct>=25?G.green:a.closePct>=15?G.yellow:G.red, fontWeight:600 }}>{a.closePct}%</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{a.avgResponseMin ? `${a.avgResponseMin}m` : '—'}</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmtUsd(a.revenue||0)}</td>
                <td style={{ padding:'6px 8px', textAlign:'right', fontWeight:700 }}>{a.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {c.accounts.length >= 2 && c.accounts[0] && (
          <div style={{ marginTop:10, padding:'10px 12px', background:G.green+'11', borderRadius:8, fontSize:12, color:G.textMuted }}>
            <strong style={{ color:G.green }}>Push winner's playbook:</strong> <em>{c.accounts[0].name}</em> is beating the cohort — clone their winning workflows, price-book, and creative to the others via 🚀 Multi-Push.
          </div>
        )}
      </div>
    ))}
  </div>;
}

// ─── Lead Marketplace dashboard ────────────────────────────────────────────
function MarketplaceDashboardTab({ toast }) {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(30);
  React.useEffect(() => {
    setData(null);
    api(`/api/lead-marketplace/dashboard?days=${days}`).then(setData).catch(e=>toast(e.message,'error'));
  }, [days]);
  if (!data) return <Spinner sz={32}/>;
  const t = data.totals || {};
  return <div>
    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
      <div style={{ fontSize:20, fontWeight:800 }}>🔀 Lead Marketplace</div>
      <div style={{ fontSize:12, color:G.textMuted, flex:1 }}>Cross-account lead distribution. Sub-accounts + external buyers bid or pay-per-lead by vertical + zip.</div>
      <Select value={String(days)} onChange={e=>setDays(Number(e.target.value))} style={{ width:120 }}>
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
      </Select>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:12, marginBottom:14 }}>
      <Stat label="Leads routed" value={t.leads||0} color={G.cyan}/>
      <Stat label="Deliveries" value={t.deliveries||0} color={G.purple}/>
      <Stat label="Gross revenue" value={fmtUsd((t.grossRevenueCents||0)/100)} color={G.green}/>
      <Stat label="Rev-share billed" value={fmtUsd((t.revenueShareBilledCents||0)/100)} color={G.green}/>
      <Stat label="Active buyers" value={t.activeBuyers||0} color={G.yellow}/>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <div style={{ ...styles.card, padding:14 }}>
        <div style={{ fontWeight:700, marginBottom:8 }}>By category</div>
        {!(data.byCategory||[]).length ? <div style={{ color:G.textMuted, fontSize:13 }}>No leads yet.</div> :
          (data.byCategory||[]).map((r,i) => <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:`1px solid ${G.border}`, fontSize:13 }}>
            <span>{r.category||'—'}</span>
            <span><strong>{r.n}</strong> · {fmtUsd((r.base_price_total_cents||0)/100)}</span>
          </div>)}
      </div>
      <div style={{ ...styles.card, padding:14 }}>
        <div style={{ fontWeight:700, marginBottom:8 }}>Top buyers</div>
        {!(data.topBuyers||[]).length ? <div style={{ color:G.textMuted, fontSize:13 }}>No buyer activity yet.</div> :
          (data.topBuyers||[]).slice(0,5).map((b,i) => <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:`1px solid ${G.border}`, fontSize:13 }}>
            <span>{b.name} <span style={{ color:G.textMuted, fontSize:11 }}>({b.type})</span></span>
            <span>{b.deliveries} · {fmtUsd((b.spent_cents||0)/100)}</span>
          </div>)}
      </div>
    </div>
    <div style={{ marginTop:14 }}>
      <BuyersDetailedPanel toast={toast}/>
    </div>
    <div style={{ marginTop:14 }}>
      <PricingModelsEditor toast={toast}/>
    </div>
  </div>;
}

// ─── Buyers Detailed panel ─────────────────────────────────────────────────
// Full roster of marketplace buyers with SaaS-tier vs external segmentation
// and per-buyer leads-purchased history. Includes a "+ Create Buyer Sub-
// Account" flow that provisions a leads_only sub-account in one click.
function BuyersDetailedPanel({ toast }) {
  const [data, setData] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [openBuyer, setOpenBuyer] = React.useState(null);
  const load = () => api('/api/lead-marketplace/buyers-detailed').then(setData).catch(e=>toast(e.message,'error'));
  React.useEffect(() => { load(); }, []);
  if (!data) return <Spinner sz={24}/>;
  const t = data.totals || {};
  return <div style={{ ...styles.card, padding:16 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
      <div>
        <div style={{ fontWeight:700, fontSize:15 }}>👥 All Buyers</div>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>SaaS sub-account buyers vs external, with per-buyer lead purchase history.</div>
      </div>
      <Btn v="primary" onClick={()=>setCreating(true)}>+ Create Buyer Sub-Account</Btn>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:10, marginBottom:14 }}>
      <Mini label="Total buyers" value={t.totalBuyers||0} color={G.cyan}/>
      <Mini label="On SaaS platform" value={t.saasBuyers||0} color={G.purple}/>
      <Mini label="External" value={t.externalBuyers||0} color={G.orange}/>
      <Mini label="Active" value={t.activeBuyers||0} color={G.green}/>
      <Mini label="30d revenue (SaaS)" value={fmtUsd((t.saasVsExternalRevenue30d?.saas||0)/100)} small color={G.purple}/>
      <Mini label="30d revenue (External)" value={fmtUsd((t.saasVsExternalRevenue30d?.external||0)/100)} small color={G.orange}/>
    </div>
    <table style={{ width:'100%', fontSize:13 }}>
      <thead><tr style={{ color:G.textMuted, fontSize:11, textTransform:'uppercase' }}>
        <th style={{ textAlign:'left', padding:'6px 8px' }}>Buyer</th>
        <th style={{ textAlign:'left', padding:'6px 8px' }}>Tier</th>
        <th style={{ textAlign:'left', padding:'6px 8px' }}>Verticals</th>
        <th style={{ textAlign:'right', padding:'6px 8px' }}>Zips</th>
        <th style={{ textAlign:'right', padding:'6px 8px' }}>Leads 30d</th>
        <th style={{ textAlign:'right', padding:'6px 8px' }}>Lifetime</th>
        <th style={{ textAlign:'right', padding:'6px 8px' }}>Spent 30d</th>
        <th style={{ textAlign:'right', padding:'6px 8px' }}>Status</th>
        <th></th>
      </tr></thead>
      <tbody>
        {(data.buyers || []).map(b => (
          <tr key={b.id} style={{ borderTop:`1px solid ${G.border}` }}>
            <td style={{ padding:'6px 8px', fontWeight:600 }}>{b.name}</td>
            <td style={{ padding:'6px 8px' }}>
              <Badge c={b.isSaasSubaccount?G.purple:G.orange} bg={(b.isSaasSubaccount?G.purple:G.orange)+'22'}>
                {b.isSaasSubaccount ? 'SaaS' : 'External'}
              </Badge>
            </td>
            <td style={{ padding:'6px 8px', fontSize:11 }}>{(b.verticals||[]).slice(0,3).join(', ') || '—'}</td>
            <td style={{ padding:'6px 8px', textAlign:'right' }}>{(b.zipCodes||[]).length}</td>
            <td style={{ padding:'6px 8px', textAlign:'right' }}>{b.deliveries30d||0}</td>
            <td style={{ padding:'6px 8px', textAlign:'right' }}>{b.lifetimeDeliveries||0}</td>
            <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmtUsd((b.spent30dCents||0)/100)}</td>
            <td style={{ padding:'6px 8px', textAlign:'right' }}>
              <Badge c={b.active?G.green:G.textMuted} bg={(b.active?G.green:G.textMuted)+'22'}>{b.active?'Active':'Paused'}</Badge>
            </td>
            <td style={{ padding:'6px 8px', textAlign:'right' }}>
              <button onClick={()=>setOpenBuyer(b)} style={{ background:'none', border:'none', color:G.cyan, cursor:'pointer', fontSize:12 }}>View leads →</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {openBuyer && <BuyerLeadsModal buyer={openBuyer} onClose={()=>setOpenBuyer(null)} toast={toast}/>}
    {creating && <CreateBuyerSubaccountModal onClose={()=>setCreating(false)} onCreated={()=>{ setCreating(false); load(); toast('✓ Buyer sub-account created + credentials emailed'); }} toast={toast}/>}
  </div>;
}

function BuyerLeadsModal({ buyer, onClose, toast }) {
  const [data, setData] = React.useState(null);
  React.useEffect(()=>{ api(`/api/lead-marketplace/buyers/${buyer.id}/leads?days=90`).then(setData).catch(e=>toast(e.message,'error')); }, [buyer.id]);
  const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:9999, display:'flex', alignItems:'flex-start', justifyContent:'center', overflow:'auto', padding:'32px 16px' };
  const card = { background:G.card, borderRadius:14, border:`1px solid ${G.border}`, width:'100%', maxWidth:900, padding:'24px 26px' };
  return <div style={overlay} onClick={onClose}>
    <div style={card} onClick={e=>e.stopPropagation()}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:800 }}>Leads delivered to {buyer.name}</div>
          <div style={{ fontSize:12, color:G.textMuted }}>{buyer.isSaasSubaccount ? 'SaaS sub-account' : 'External buyer'} · last 90 days</div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:G.textMuted, fontSize:22, cursor:'pointer' }}>×</button>
      </div>
      {!data ? <Spinner sz={24}/> : (data.deliveries||[]).length === 0 ? (
        <div style={{ color:G.textMuted, fontSize:13, padding:14, textAlign:'center' }}>No deliveries in this window.</div>
      ) : (
        <table style={{ width:'100%', fontSize:13 }}>
          <thead><tr style={{ color:G.textMuted, fontSize:11, textTransform:'uppercase' }}>
            <th style={{ textAlign:'left', padding:'6px 8px' }}>When</th>
            <th style={{ textAlign:'left', padding:'6px 8px' }}>Lead</th>
            <th style={{ textAlign:'left', padding:'6px 8px' }}>Vertical / Category</th>
            <th style={{ textAlign:'left', padding:'6px 8px' }}>Location</th>
            <th style={{ textAlign:'left', padding:'6px 8px' }}>Model</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Charged</th>
            <th style={{ textAlign:'right', padding:'6px 8px' }}>Status</th>
          </tr></thead>
          <tbody>
            {(data.deliveries||[]).map(d => (
              <tr key={d.id} style={{ borderTop:`1px solid ${G.border}` }}>
                <td style={{ padding:'6px 8px' }}>{new Date(d.created_at).toLocaleDateString()}</td>
                <td style={{ padding:'6px 8px' }}>{d.first_name} {d.last_name}<br/><span style={{ color:G.textMuted, fontSize:11 }}>{d.phone}</span></td>
                <td style={{ padding:'6px 8px' }}>{d.vertical||'—'}<br/><span style={{ color:G.textMuted, fontSize:11 }}>{d.category||''}</span></td>
                <td style={{ padding:'6px 8px' }}>{d.city}{d.state?', '+d.state:''}</td>
                <td style={{ padding:'6px 8px' }}>{d.pricing_model||'—'}</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmtUsd((d.charged_cents||0)/100)}</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{d.status||''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>;
}

function CreateBuyerSubaccountModal({ onClose, onCreated, toast }) {
  const [form, setForm] = React.useState({
    businessName: '', ownerFirstName: '', ownerLastName: '', ownerEmail: '', ownerPhone: '',
    verticals: [], zipCodes: '',
    homeCity: '', homeState: '', homeZip: '',
    maxLeadsPerDay: 20, maxBidCents: 5000,
    preferredPricingModels: ['exclusive','shared','per_booked','per_revenue'],
  });
  const [saving, setSaving] = React.useState(false);
  const VERTICALS = ['HVAC','Plumbing','Electrical','Roofing','Garage Doors','Handyman','Landscaping','Pest Control','Cleaning','Insurance','Real Estate','Other'];
  const MODELS = ['exclusive','shared','per_booked','per_revenue'];
  const toggle = (arrKey, val) => {
    const cur = form[arrKey] || [];
    setForm({ ...form, [arrKey]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] });
  };
  const submit = async () => {
    if (!form.businessName || !form.ownerEmail) return toast('Business name + owner email required', 'error');
    if (!form.verticals.length) return toast('Pick at least one vertical', 'error');
    const zips = String(form.zipCodes).split(/[,\s]+/).filter(z => /^\d{5}$/.test(z));
    if (!zips.length) return toast('Add at least one 5-digit zip', 'error');
    setSaving(true);
    try {
      const r = await api('/api/lead-marketplace/create-buyer-subaccount', { method:'POST', body:{
        ...form, zipCodes: zips,
      }});
      alert(`✅ Created!\n\nBusiness: ${form.businessName}\nOwner login: ${form.ownerEmail}\nTemp password: ${r.tempPassword}\n\nCredentials emailed to owner. They log in and see only the Get New Leads tab.`);
      onCreated();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  };
  const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:9999, display:'flex', alignItems:'flex-start', justifyContent:'center', overflow:'auto', padding:'32px 16px' };
  const card = { background:G.card, borderRadius:14, border:`1px solid ${G.border}`, width:'100%', maxWidth:720, padding:'24px 26px' };
  return <div style={overlay} onClick={onClose}>
    <div style={card} onClick={e=>e.stopPropagation()}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
        <div style={{ fontSize:20, fontWeight:800 }}>+ Create Buyer Sub-Account</div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:G.textMuted, fontSize:22, cursor:'pointer' }}>×</button>
      </div>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:14 }}>Creates a TradeOps sub-account on the <b>leads_only</b> plan tier. The buyer logs in and sees only the "Get New Leads" tab — CRM, dispatch, and finance are hidden.</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <Field label="Business name"><Input value={form.businessName} onChange={e=>setForm({...form, businessName:e.target.value})}/></Field>
        <Field label="Owner email"><Input value={form.ownerEmail} onChange={e=>setForm({...form, ownerEmail:e.target.value})}/></Field>
        <Field label="Owner first name"><Input value={form.ownerFirstName} onChange={e=>setForm({...form, ownerFirstName:e.target.value})}/></Field>
        <Field label="Owner last name"><Input value={form.ownerLastName} onChange={e=>setForm({...form, ownerLastName:e.target.value})}/></Field>
        <Field label="Owner phone"><Input value={form.ownerPhone} onChange={e=>setForm({...form, ownerPhone:e.target.value})}/></Field>
        <Field label="Home city"><Input value={form.homeCity} onChange={e=>setForm({...form, homeCity:e.target.value})}/></Field>
        <Field label="State"><Input value={form.homeState} onChange={e=>setForm({...form, homeState:e.target.value})}/></Field>
        <Field label="Home zip"><Input value={form.homeZip} onChange={e=>setForm({...form, homeZip:e.target.value})}/></Field>
      </div>
      <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', margin:'12px 0 6px' }}>Verticals they'll buy</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
        {VERTICALS.map(v => {
          const on = form.verticals.includes(v);
          return <button key={v} onClick={()=>toggle('verticals', v)}
            style={{ padding:'4px 10px', borderRadius:14, border:`1px solid ${on?G.purple:G.border}`, background: on?G.purpleDim:G.card, color: on?G.purple:G.text, fontSize:11, cursor:'pointer' }}>{v}</button>;
        })}
      </div>
      <Field label="Zip codes (comma or space separated)">
        <Input value={form.zipCodes} onChange={e=>setForm({...form, zipCodes:e.target.value})} placeholder="43215 43220 43221"/>
      </Field>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <Field label="Max leads/day"><Input type="number" value={form.maxLeadsPerDay} onChange={e=>setForm({...form, maxLeadsPerDay:e.target.value})}/></Field>
        <Field label="Max bid per lead (cents)"><Input type="number" value={form.maxBidCents} onChange={e=>setForm({...form, maxBidCents:e.target.value})}/></Field>
      </div>
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn v="primary" onClick={submit} disabled={saving}>{saving?'Creating…':'Create + Email Credentials'}</Btn>
      </div>
    </div>
  </div>;
}

// ─── Pricing Models editor ─────────────────────────────────────────────────
// Kyle sets our cost per category + markups for subaccount vs external buyers.
// Buyer price is derived: our_cost * markup(buyer_type). Sub-account default
// 1.25x, external 2x. Fully adjustable here.
function PricingModelsEditor({ toast }) {
  const [settings, setSettings] = React.useState(null);
  React.useEffect(() => {
    api('/api/lead-marketplace/settings').then(setSettings).catch(e=>toast(e.message,'error'));
  }, []);
  const save = async () => {
    try {
      await api('/api/lead-marketplace/settings', { method:'PATCH', body:{
        pricing_models: settings.pricing_models,
        answer_buffer_seconds: Number(settings.answer_buffer_seconds) || 30,
        ring_all_enabled: settings.ring_all_enabled ? 1 : 0,
      }});
      toast('✓ Marketplace settings saved');
    } catch (e) { toast(e.message, 'error'); }
  };
  if (!settings) return <Spinner sz={24}/>;
  const pm = settings.pricing_models || { markups:{subaccount:1.25, external:2.0}, categories:{} };
  const setPM = (path, val) => {
    const next = JSON.parse(JSON.stringify(pm));
    let cur = next;
    for (let i=0; i<path.length-1; i++) { cur[path[i]] = cur[path[i]] ?? {}; cur = cur[path[i]]; }
    cur[path[path.length-1]] = val;
    setSettings({ ...settings, pricing_models: next });
  };
  const MODEL_KEYS = [
    { id:'exclusive',   label:'Exclusive' },
    { id:'shared',      label:'Shared' },
    { id:'per_booked',  label:'Per Booked' },
    { id:'per_revenue', label:'Per Revenue' },
  ];
  return <div style={{ ...styles.card, padding:16 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
      <div>
        <div style={{ fontWeight:700, fontSize:15 }}>💵 Pricing Models</div>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>Our cost per lead + markup by buyer type. Sub-accounts (system) get a lower markup; external buyers pay more.</div>
      </div>
      <Btn v="primary" onClick={save}>Save Pricing</Btn>
    </div>

    {/* Markups row */}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
      <Field label="Sub-account markup (× our cost)">
        <Input type="number" step="0.05" value={pm.markups?.subaccount ?? 1.25}
          onChange={e=>setPM(['markups','subaccount'], Number(e.target.value)||1.25)}/>
      </Field>
      <Field label="External buyer markup (× our cost)">
        <Input type="number" step="0.05" value={pm.markups?.external ?? 2.0}
          onChange={e=>setPM(['markups','external'], Number(e.target.value)||2.0)}/>
      </Field>
    </div>

    {/* Ring-all + buffer row */}
    <div style={{ ...styles.card, padding:12, marginBottom:16, background:G.input }}>
      <div style={{ fontSize:12, color:G.textMuted, textTransform:'uppercase', fontWeight:700, marginBottom:8 }}>Multi-Buyer Ring</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, alignItems:'center' }}>
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
          <input type="checkbox" checked={!!settings.ring_all_enabled} onChange={e=>setSettings({ ...settings, ring_all_enabled: e.target.checked ? 1 : 0 })}/>
          <div>
            <div style={{ fontWeight:600, fontSize:13 }}>Ring all eligible buyers</div>
            <div style={{ fontSize:11, color:G.textMuted }}>First to accept + hold past buffer wins. Others auto-void.</div>
          </div>
        </label>
        <Field label="Answer buffer (seconds)">
          <Input type="number" min="5" max="300" value={settings.answer_buffer_seconds ?? 30}
            onChange={e=>setSettings({ ...settings, answer_buffer_seconds: Number(e.target.value) || 30 })}/>
        </Field>
      </div>
    </div>

    {/* Per-category grid */}
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700, marginBottom:6 }}>Per Category</div>
    {Object.entries(pm.categories || {}).map(([cat, catCfg]) => (
      <div key={cat} style={{ ...styles.card, padding:12, marginBottom:10, background:G.input }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
          <strong style={{ fontSize:14, textTransform:'capitalize', minWidth:110 }}>{cat}</strong>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:12, color:G.textMuted }}>Our cost (¢):</span>
            <Input type="number" value={catCfg.our_cost_cents || 0} style={{ width:100 }}
              onChange={e=>setPM(['categories', cat, 'our_cost_cents'], Number(e.target.value)||0)}/>
            <span style={{ fontSize:12, color:G.textMuted }}>= sub ${(((catCfg.our_cost_cents||0)*(pm.markups?.subaccount||1.25))/100).toFixed(0)} · ext ${(((catCfg.our_cost_cents||0)*(pm.markups?.external||2))/100).toFixed(0)}</span>
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8 }}>
          {MODEL_KEYS.map(m => {
            const mCfg = catCfg.models?.[m.id] || { enabled: false };
            return <div key={m.id} style={{ padding:8, background:G.card, borderRadius:6, border:`1px solid ${G.border}` }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', marginBottom:4 }}>
                <input type="checkbox" checked={!!mCfg.enabled}
                  onChange={e=>setPM(['categories', cat, 'models', m.id, 'enabled'], e.target.checked)}/>
                <strong style={{ fontSize:12 }}>{m.label}</strong>
              </label>
              {m.id === 'shared' && (
                <div style={{ fontSize:11, color:G.textMuted }}>Discount %:
                  <Input type="number" value={mCfg.discount_pct ?? 40} style={{ width:60, marginLeft:4 }}
                    onChange={e=>setPM(['categories', cat, 'models', m.id, 'discount_pct'], Number(e.target.value)||0)}/>
                </div>
              )}
              {m.id === 'per_booked' && (
                <div style={{ fontSize:11, color:G.textMuted }}>Markup ×:
                  <Input type="number" step="0.1" value={mCfg.markup_multiplier ?? 2.5} style={{ width:60, marginLeft:4 }}
                    onChange={e=>setPM(['categories', cat, 'models', m.id, 'markup_multiplier'], Number(e.target.value)||0)}/>
                </div>
              )}
              {m.id === 'per_revenue' && (
                <div style={{ fontSize:11, color:G.textMuted }}>% rev:
                  <Input type="number" value={mCfg.pct ?? 15} style={{ width:60, marginLeft:4 }}
                    onChange={e=>setPM(['categories', cat, 'models', m.id, 'pct'], Number(e.target.value)||0)}/>
                </div>
              )}
            </div>;
          })}
        </div>
      </div>
    ))}
  </div>;
}

// ─── TCPA / Consent audit ──────────────────────────────────────────────────
function ConsentAuditTab({ toast }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api('/api/agency/consent-audit').then(setData).catch(e=>toast(e.message,'error'));
  }, []);
  if (!data) return <Spinner sz={32}/>;
  return <div>
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:20, fontWeight:800 }}>✅ Consent Audit</div>
      <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>TCPA / EBR compliance across every sub-account. Red = missing consent records or EBR clock expiring soon.</div>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12, marginBottom:14 }}>
      <Stat label="Total contacts" value={data.summary?.totalContacts||0} color={G.cyan}/>
      <Stat label="With consent record" value={data.summary?.withConsent||0} color={G.green}/>
      <Stat label="Missing consent" value={data.summary?.missingConsent||0} color={(data.summary?.missingConsent||0)>0?G.red:G.textMuted}/>
      <Stat label="EBR expiring &lt;30d" value={data.summary?.ebrExpiringSoon||0} color={(data.summary?.ebrExpiringSoon||0)>0?G.yellow:G.textMuted}/>
      <Stat label="DNC / opted-out" value={data.summary?.optedOut||0} color={G.textMuted}/>
    </div>
    <div style={{ ...styles.card, padding:14 }}>
      <div style={{ fontWeight:700, marginBottom:8 }}>Per-account</div>
      <table style={{ width:'100%', fontSize:13 }}>
        <thead><tr style={{ color:G.textMuted, fontSize:11, textTransform:'uppercase' }}>
          <th style={{ textAlign:'left', padding:'6px 8px' }}>Account</th>
          <th style={{ textAlign:'right', padding:'6px 8px' }}>Contacts</th>
          <th style={{ textAlign:'right', padding:'6px 8px' }}>With consent</th>
          <th style={{ textAlign:'right', padding:'6px 8px' }}>Missing</th>
          <th style={{ textAlign:'right', padding:'6px 8px' }}>EBR &lt;30d</th>
          <th style={{ textAlign:'right', padding:'6px 8px' }}>Status</th>
        </tr></thead>
        <tbody>
          {(data.accounts||[]).map(a => {
            const pct = a.contacts ? Math.round((a.withConsent/a.contacts)*100) : 100;
            const color = pct >= 95 ? G.green : pct >= 70 ? G.yellow : G.red;
            return <tr key={a.id} style={{ borderTop:`1px solid ${G.border}` }}>
              <td style={{ padding:'6px 8px', fontWeight:600 }}>{a.name}</td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>{a.contacts}</td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>{a.withConsent}</td>
              <td style={{ padding:'6px 8px', textAlign:'right', color: a.missingConsent>0?G.red:G.textMuted }}>{a.missingConsent}</td>
              <td style={{ padding:'6px 8px', textAlign:'right', color: a.ebrExpiring>0?G.yellow:G.textMuted }}>{a.ebrExpiring}</td>
              <td style={{ padding:'6px 8px', textAlign:'right', color, fontWeight:700 }}>{pct}%</td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>
  </div>;
}

// ─── A2P 10DLC — per-account landing pages + Twilio brand/campaign submission ──
// Bumped each time the A2P tab JS changes so the owner can verify their
// browser actually loaded the latest bundle. If they see an older number
// after a deploy it's a cache problem, not a code problem.
const A2P_BUILD = 'v3 (prompt-based, Jun 4)';
function A2PTab({ toast }) {
  const [accounts, setAccounts] = useState(null);
  const [working, setWorking] = useState({});
  const load = () => api('/api/agency/accounts').then(setAccounts).catch(e=>toast(e.message,'error'));
  React.useEffect(() => { load(); }, []);
  const callForAccount = async (acctId, endpoint, label) => {
    setWorking(w => ({ ...w, [acctId+endpoint]: true }));
    try {
      const r = await api(`/api/a2p/${endpoint}`, { method:'POST', headers:{ 'X-Ghost-Account': acctId } });
      toast(`${label}: ${r.brandSid || r.campaignSid || r.slug || 'OK'}`);
      load();
    } catch(e) { toast(e.message,'error'); }
    finally { setWorking(w => ({ ...w, [acctId+endpoint]: false })); }
  };
  const bulkSlugs = async () => {
    try {
      const r = await api('/api/a2p/bulk/ensure-slugs', { method:'POST' });
      const msg = r.slugged > 0
        ? `✓ Slugged ${r.slugged} new account${r.slugged===1?'':'s'} · ${r.accountsWithSlug}/${r.total} total now have slugs`
        : `All ${r.total} account${r.total===1?'':'s'} already have slugs — nothing to do.`;
      toast(msg);
      load();
    } catch(e) { toast(e.message,'error'); }
  };
  const copyAll = (urls) => {
    const txt = `Opt-in URL: ${urls.landing}\nTerms: ${urls.terms}\nPrivacy: ${urls.privacy}\nSample messages: ${urls.sampleMessages}`;
    navigator.clipboard.writeText(txt).then(()=>toast('Copied all 4 URLs')).catch(()=>{});
  };
  return <div>
    <div style={{ fontSize:10, color:G.textMuted, marginBottom:8, textAlign:'right' }}>
      A2P Console build: <strong style={{ color:G.text }}>{A2P_BUILD}</strong> · If this doesn't read "v3" you're on a cached bundle — Cmd+Shift+R to refresh.
    </div>
    <div style={{ ...styles.card, padding:16, marginBottom:14, background:G.purpleDim+'33', borderColor:G.purple+'66' }}>
      <strong style={{ color:G.purple, fontSize:13 }}>How this works</strong>
      <div style={{ fontSize:13, color:G.text, marginTop:6, lineHeight:1.55 }}>
        Every sub-account automatically gets four A2P-compliant pages templated with their business name, phone, email, and address. Submit each account's <strong>Brand</strong> + <strong>Campaign</strong> to Twilio with one click — payloads built from the account's profile.
      </div>
      <div style={{ marginTop:10 }}>
        <Btn sz="sm" onClick={bulkSlugs}>🔗 Generate URL slugs for all accounts</Btn>
      </div>
    </div>
    {!accounts ? <Spinner sz={28}/> : !accounts.length ? <div style={{ color:G.textMuted, padding:20 }}>No sub-accounts yet.</div> :
      accounts.map(a => <AccountA2PRow key={a.id} acct={a} working={working} toast={toast} onAction={callForAccount} onCopyAll={copyAll}/>)}
  </div>;
}

// Inline editor for the two campaign-time inputs Twilio needs: the human
// owner's name (appears verbatim in the sample messages) and a Google Drive
// URL with screenshots of the landing/terms/privacy pages (reviewer
// fallback if Twilio's network blocks our public URLs).
function CampaignInputs({ info, acctId, toast, onSaved }) {
  const [firstName, setFirstName] = useState(info?.ownerFirstName || '');
  const [lastName, setLastName] = useState(info?.ownerLastName || '');
  const [screenshotsUrl, setScreenshotsUrl] = useState(info?.screenshotsUrl || '');
  const [savingOwner, setSavingOwner] = useState(false);
  const [savingScreens, setSavingScreens] = useState(false);
  const saveOwner = async () => {
    setSavingOwner(true);
    try {
      await api('/api/a2p/set-owner-name', { method:'POST', headers:{ 'X-Ghost-Account': acctId }, body:{ firstName, lastName }});
      toast(`✓ Owner: ${firstName} ${lastName}`);
      onSaved && onSaved();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSavingOwner(false); }
  };
  const saveScreens = async () => {
    setSavingScreens(true);
    try {
      await api('/api/a2p/set-screenshots-url', { method:'POST', headers:{ 'X-Ghost-Account': acctId }, body:{ url: screenshotsUrl.trim() }});
      toast('✓ Screenshots URL saved');
      onSaved && onSaved();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSavingScreens(false); }
  };
  return <div style={{ marginBottom:14, padding:12, background:G.input, borderRadius:8 }}>
    <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:10 }}>
      Campaign inputs (set before submitting)
    </div>
    <div style={{ marginBottom:10 }}>
      <div style={{ fontSize:11, color:G.textMuted, marginBottom:4 }}>Owner name — appears verbatim in sample messages (e.g. "Hi John! This is {firstName||'…'} {lastName||'…'}")</div>
      <div style={{ display:'flex', gap:6 }}>
        <Input value={firstName} onChange={e=>setFirstName(e.target.value)} placeholder="First name" style={{ flex:1 }}/>
        <Input value={lastName} onChange={e=>setLastName(e.target.value)} placeholder="Last name" style={{ flex:1 }}/>
        <Btn sz="xs" v="primary" onClick={saveOwner} disabled={savingOwner || (!firstName && !lastName)}>
          {savingOwner ? <Spinner sz={10}/> : 'Save'}
        </Btn>
      </div>
    </div>
    <div>
      <div style={{ fontSize:11, color:G.textMuted, marginBottom:4 }}>
        Screenshots URL (Google Drive folder with screenshots of landing, terms, privacy — set "Anyone with the link can view")
      </div>
      <div style={{ display:'flex', gap:6 }}>
        <Input value={screenshotsUrl} onChange={e=>setScreenshotsUrl(e.target.value)} placeholder="https://drive.google.com/drive/folders/…" style={{ flex:1 }}/>
        <Btn sz="xs" v="primary" onClick={saveScreens} disabled={savingScreens}>
          {savingScreens ? <Spinner sz={10}/> : 'Save'}
        </Btn>
      </div>
      <div style={{ fontSize:10, color:G.textMuted, marginTop:4 }}>
        Optional — included in the campaign Message Flow as a fallback if Twilio's reviewers can't reach the live URLs.
      </div>
    </div>
  </div>;
}

function AccountA2PRow({ acct, working, toast, onAction, onCopyAll }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [brandSidInput, setBrandSidInput] = useState('');
  const brandSidRef = React.useRef(null);
  const [importing, setImporting] = useState(false);
  const load = () => api('/api/a2p/info', { headers:{ 'X-Ghost-Account': acct.id }}).then(setInfo).catch(()=>{});
  React.useEffect(() => { load(); }, [acct.id]);
  const refreshStatus = async () => {
    try { await api('/api/a2p/refresh-status', { method:'POST', headers:{ 'X-Ghost-Account': acct.id }}); load(); toast('Status refreshed'); }
    catch(e) { toast(e.message,'error'); }
  };
  const importBrand = async () => {
    // Native browser prompt — guaranteed to capture pasted content with no
    // React state, no event handlers, no input element to fight with.
    const raw = window.prompt('Paste the Brand SID from Twilio Console:', '') || '';
    if (!raw.trim()) return; // user cancelled
    const sid = raw.replace(/[^A-Za-z0-9]/g, '').trim();
    if (!/^BN[A-Za-z0-9]{10,}$/.test(sid)) {
      toast(`Brand SID must start with BN. Got: "${sid.slice(0,16)}…" (${sid.length} chars). Try again.`, 'error');
      return;
    }
    setImporting(true);
    try {
      await api('/api/a2p/set-brand-sid', { method:'POST', headers:{ 'X-Ghost-Account': acct.id }, body:{ brandSid: sid, brandStatus: 'APPROVED' }});
      toast('✓ Brand linked — you can submit the campaign now');
      setBrandSidInput('');
      load();
    } catch(e) { toast(e.message,'error'); }
    finally { setImporting(false); }
  };
  const statusBadge = (status) => {
    if (!status) return <Badge c={G.textMuted} bg={G.input}>Not submitted</Badge>;
    const ok = /APPROVED|REGISTERED|VERIFIED/i.test(status);
    const pending = /PENDING|REVIEW|IN_PROGRESS/i.test(status);
    const c = ok ? G.green : pending ? G.yellow : G.red;
    return <Badge c={c} bg={c+'22'}>{status}</Badge>;
  };
  const UrlLink = ({ label, url, short }) => (
    <a href={url} target="_blank" rel="noreferrer" onClick={(e)=>e.stopPropagation()}
       style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'4px 8px', background:G.input, borderRadius:6, fontSize:11, color:G.cyan, textDecoration:'none', maxWidth:'100%' }}>
      <span style={{ color:G.textMuted, fontWeight:600 }}>{label}</span>
      <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>↗</span>
    </a>
  );
  return <div style={{ ...styles.card, padding:14, marginBottom:10 }}>
    {/* Header row: name + status badges */}
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:8 }}>
      <div style={{ flex:1, minWidth:0 }}>
        <strong style={{ fontSize:15 }}>{acct.name || acct.brand_name || '(unnamed)'}</strong>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{info?.slug ? `/lp/a/${info.slug}` : 'No slug yet'}</div>
      </div>
      <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
        <span style={{ fontSize:10, color:G.textMuted }}>Brand</span>{statusBadge(info?.brand?.status)}
        <span style={{ fontSize:10, color:G.textMuted, marginLeft:4 }}>Campaign</span>{statusBadge(info?.campaign?.status)}
      </div>
    </div>

    {/* URL chips — ALWAYS visible right under the company name. Click ↗ to
        open, or use the Copy buttons to grab for pasting into Twilio. */}
    {info?.urls && <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
      <UrlLink label="Landing" url={info.urls.landing}/>
      <UrlLink label="Terms" url={info.urls.terms}/>
      <UrlLink label="Privacy" url={info.urls.privacy}/>
      <UrlLink label="Sample msgs" url={info.urls.sampleMessages}/>
      <Btn sz="xs" v="ghost" onClick={()=>onCopyAll(info.urls)}>📋 Copy all</Btn>
      <Btn sz="xs" v="ghost" onClick={()=>setOpen(!open)}>{open?'Hide actions ▴':'Actions ▾'}</Btn>
    </div>}

    {open && <div style={{ marginTop:6, paddingTop:12, borderTop:`1px solid ${G.border}` }}>
      {/* Brand import — for accounts where the brand was approved in the
          Twilio Console outside our auto-submit flow. Click the button →
          native prompt → paste → done. No input element to fight with. */}
      {!info?.brand?.sid && <div style={{ marginBottom:14, padding:10, background:G.input, borderRadius:8 }}>
        <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>
          Brand already approved on the Twilio Console?
        </div>
        <Btn sz="sm" v="primary" onClick={importBrand} disabled={importing} full>
          {importing ? <Spinner sz={12}/> : '📋 Paste Brand SID'}
        </Btn>
        <div style={{ fontSize:10, color:G.textMuted, marginTop:6 }}>
          Get it from Twilio Console → Messaging → Regulatory Compliance → A2P 10DLC → Brands. Click the button → paste the SID into the popup → click OK.
        </div>
      </div>}

      {/* Campaign inputs the owner sets BEFORE Submit Campaign. The values
          flow verbatim into the Twilio campaign description + sample messages. */}
      {info?.brand?.sid && !info?.campaign?.sid && <CampaignInputs info={info} acctId={acct.id} toast={toast} onSaved={()=>{}}/>}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        <Btn sz="sm" v="primary" onClick={()=>onAction(acct.id, 'submit-brand', 'Brand submitted')} disabled={working[acct.id+'submit-brand'] || info?.brand?.sid}>
          {info?.brand?.sid ? `✓ Brand: ${info.brand.sid.slice(0,10)}…` : '📤 Submit Brand to Twilio'}
        </Btn>
        <Btn sz="sm" v="primary" onClick={()=>onAction(acct.id, 'submit-campaign', 'Campaign submitted')} disabled={working[acct.id+'submit-campaign'] || !info?.brand?.sid || info?.campaign?.sid}>
          {info?.campaign?.sid ? `✓ Campaign: ${info.campaign.sid.slice(0,10)}…` : '📤 Submit Campaign'}
        </Btn>
        <Btn sz="sm" onClick={refreshStatus}>🔄 Refresh status</Btn>
      </div>
      {info?.lastError && <div style={{ marginTop:10, padding:'8px 10px', background:G.red+'22', color:G.red, borderRadius:6, fontSize:11 }}>
        Last error: {info.lastError}
      </div>}
      {!info?.twilioConfigured && <div style={{ marginTop:10, padding:'8px 10px', background:G.yellow+'22', color:G.yellow, borderRadius:6, fontSize:11 }}>
        Twilio account credentials not configured — submissions will return dry-run payload.
      </div>}
    </div>}
  </div>;
}

// ─── Backups (super-admin only) ────────────────────────────────────────────
function AgencyBackups({ toast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api('/api/admin-panel/backups').then(setData).catch(e=>toast(e.message,'error'));
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setBusy(true);
    try { const r = await api('/api/admin-panel/backups/run-now', { method:'POST' }); toast(`✓ Snapshot saved (${(r.sizeBytes/1024/1024).toFixed(2)} MB)`); load(); }
    catch(e) { toast(e.message,'error'); } finally { setBusy(false); }
  };
  if (!data) return <Spinner sz={32}/>;
  return <div>
    <div style={{ ...styles.card, marginBottom:14 }}>
      <h3 style={{ margin:'0 0 10px 0' }}>💾 Backup Status</h3>
      <div style={{ display:'flex', gap:24, fontSize:13 }}>
        <div><strong>Encryption:</strong> {data.encryptionEnabled ? <Badge c={G.green} bg={G.greenLight}>AES-256-GCM</Badge> : <Badge c={G.yellow} bg={G.yellowLight}>Disabled (set BACKUP_ENCRYPTION_KEY)</Badge>}</div>
        <div><strong>Offsite:</strong> {data.offsiteEnabled ? <Badge c={G.green} bg={G.greenLight}>S3/B2 enabled</Badge> : <Badge c={G.textMuted} bg={G.input}>Local only</Badge>}</div>
      </div>
      <div style={{ marginTop:14 }}>
        <Btn v="primary" onClick={runNow} disabled={busy}>{busy?<Spinner sz={14}/>:'Run Backup Now'}</Btn>
      </div>
    </div>
    <div style={styles.card}>
      <h3 style={{ margin:'0 0 12px 0' }}>Snapshots ({data.snapshots.length})</h3>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>Each download is AES-256-GCM encrypted if encryption is enabled. Keep one on a flash drive offline.</div>
      <table style={{ width:'100%', fontSize:13 }}>
        <thead><tr><th style={styles.th}>File</th><th style={styles.th}>Size</th><th style={styles.th}>SHA-256</th><th style={styles.th}>Modified</th><th style={styles.th}></th></tr></thead>
        <tbody>{data.snapshots.map(s => (
          <tr key={s.file}>
            <td style={{ ...styles.td, fontFamily:'monospace', fontSize:11 }}>{s.file}</td>
            <td style={styles.td}>{(s.sizeBytes/1024/1024).toFixed(2)} MB</td>
            <td style={{ ...styles.td, fontFamily:'monospace', fontSize:11, color:G.textMuted }}>{s.sha256?.slice(0,12) || '—'}…</td>
            <td style={styles.td}>{new Date(s.modifiedAt).toLocaleString()}</td>
            <td style={styles.td}>
              <a href={`/api/admin-panel/backups/download/${encodeURIComponent(s.file)}`} download>
                <Btn sz="xs" v="primary">⬇ Download</Btn>
              </a>
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  </div>;
}

// ─── Network Winners (super-admin only) ────────────────────────────────────
function AgencyNetworkWinners({ toast }) {
  const [data, setData] = useState(null);
  useEffect(() => { api('/api/admin-panel/network-winners').then(setData).catch(e=>toast(e.message,'error')); }, []);
  if (!data) return <Spinner sz={32}/>;
  const Row = ({ r, color }) => (
    <tr>
      <td style={styles.td}><Badge c={color} bg={color+'22'}>{r.kind}</Badge></td>
      <td style={{ ...styles.td, fontSize:12 }}>{(r.sample_body||'').slice(0,140)}</td>
      <td style={styles.td}>{r.attempts}</td>
      <td style={styles.td}>{r.conversions}</td>
      <td style={{ ...styles.td, fontWeight:700, color }}>{(r.conversion_rate*100).toFixed(1)}%</td>
    </tr>
  );
  return <div>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      Cross-account performance of every message template. Winners auto-suggest themselves to accounts performing below network median. Losers are quietly retired.
    </div>
    <div style={{ ...styles.card, marginBottom:14 }}>
      <h3 style={{ color:G.green, margin:'0 0 12px 0' }}>🏆 Winners ({data.winners?.length||0})</h3>
      <table style={{ width:'100%' }}><thead><tr><th style={styles.th}>Kind</th><th style={styles.th}>Template</th><th style={styles.th}>Tries</th><th style={styles.th}>Wins</th><th style={styles.th}>Rate</th></tr></thead>
      <tbody>{(data.winners||[]).map(r => <Row key={r.template_hash} r={r} color={G.green}/>)}</tbody></table>
    </div>
    <div style={{ ...styles.card, marginBottom:14 }}>
      <h3 style={{ color:G.red, margin:'0 0 12px 0' }}>💀 Deprecated ({data.deprecated?.length||0})</h3>
      <table style={{ width:'100%' }}><thead><tr><th style={styles.th}>Kind</th><th style={styles.th}>Template</th><th style={styles.th}>Tries</th><th style={styles.th}>Wins</th><th style={styles.th}>Rate</th></tr></thead>
      <tbody>{(data.deprecated||[]).map(r => <Row key={r.template_hash} r={r} color={G.red}/>)}</tbody></table>
    </div>
    <div style={styles.card}>
      <h3 style={{ color:G.textMuted, margin:'0 0 12px 0' }}>📊 Active — Learning ({data.active?.length||0})</h3>
      <table style={{ width:'100%' }}><thead><tr><th style={styles.th}>Kind</th><th style={styles.th}>Template</th><th style={styles.th}>Tries</th><th style={styles.th}>Wins</th><th style={styles.th}>Rate</th></tr></thead>
      <tbody>{(data.active||[]).slice(0,50).map(r => <Row key={r.template_hash} r={r} color={G.textMuted}/>)}</tbody></table>
    </div>
  </div>;
}

function AccountsTab({ toast, user }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [featuresFor, setFeaturesFor] = useState(null);
  const [crmFor, setCrmFor] = useState(null); // open mini-CRM drawer for a sub-account
  const [blasting, setBlasting] = useState(false);
  const [agencyNotifs, setAgencyNotifs] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api('/api/agency/accounts').then(setAccounts).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  const loadNotifs = () => api('/api/agency/notifications').then(setAgencyNotifs).catch(()=>{});
  useEffect(() => { load(); loadNotifs(); const t = setInterval(loadNotifs, 60000); return ()=>clearInterval(t); }, []);

  const enterAccount = async a => {
    try { await api('/api/auth/ghost', { method:'POST', body:{ accountId: a.id } });
      window.location.reload();
    } catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <Spinner sz={32}/>;

  const totalMrr = accounts.reduce((s,a)=>s+(a.mrr||0), 0);
  const totalRev = accounts.reduce((s,a)=>s+(a.stats?.collected||0), 0);

  // Filter active vs archived for the grid
  const displayedAccounts = showArchived ? accounts : accounts.filter(a => a.status !== 'archived');

  return <>
    {agencyNotifs && (agencyNotifs.counts.pastDue + agencyNotifs.counts.trialEnding + agencyNotifs.counts.lowCredits + agencyNotifs.counts.unreadOwnerMsgs > 0) && (
      <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.yellow+'66', background:G.yellow+'08' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <strong style={{ fontSize:14, color:G.yellow }}>🔔 Needs your attention</strong>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:10 }}>
          {agencyNotifs.counts.unreadOwnerMsgs > 0 && (
            <div style={{ ...styles.card, padding:10, background:G.purpleDim }}>
              <div style={{ fontSize:11, color:G.purple, fontWeight:700, textTransform:'uppercase' }}>💬 Unread owner replies</div>
              <div style={{ fontSize:18, fontWeight:800, marginTop:4 }}>{agencyNotifs.counts.unreadOwnerMsgs}</div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>
                {agencyNotifs.unreadOwnerMsgs.slice(0,3).map(u => (
                  <button key={u.accountId} onClick={()=>setCrmFor(accounts.find(a=>a.id===u.accountId))}
                    style={{ display:'block', background:'none', border:'none', color:G.cyan, cursor:'pointer', padding:'1px 0', fontSize:11, textAlign:'left' }}>
                    {u.name} · {u.count} unread
                  </button>
                ))}
              </div>
            </div>
          )}
          {agencyNotifs.counts.pastDue > 0 && (
            <div style={{ ...styles.card, padding:10, background:G.red+'22' }}>
              <div style={{ fontSize:11, color:G.red, fontWeight:700, textTransform:'uppercase' }}>⚠ Past due / canceled</div>
              <div style={{ fontSize:18, fontWeight:800, marginTop:4 }}>{agencyNotifs.counts.pastDue}</div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>
                {agencyNotifs.pastDue.slice(0,3).map(p => (
                  <button key={p.id} onClick={()=>setCrmFor(accounts.find(a=>a.id===p.id))}
                    style={{ display:'block', background:'none', border:'none', color:G.cyan, cursor:'pointer', padding:'1px 0', fontSize:11, textAlign:'left' }}>
                    {p.name} · {p.subscription_status}
                  </button>
                ))}
              </div>
            </div>
          )}
          {agencyNotifs.counts.trialEnding > 0 && (
            <div style={{ ...styles.card, padding:10, background:G.yellow+'22' }}>
              <div style={{ fontSize:11, color:G.yellow, fontWeight:700, textTransform:'uppercase' }}>⏰ Trial ending soon</div>
              <div style={{ fontSize:18, fontWeight:800, marginTop:4 }}>{agencyNotifs.counts.trialEnding}</div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>{agencyNotifs.trialEnding.map(t=>t.name).slice(0,3).join(', ')}</div>
            </div>
          )}
          {agencyNotifs.counts.lowCredits > 0 && (
            <div style={{ ...styles.card, padding:10, background:G.cyan+'22' }}>
              <div style={{ fontSize:11, color:G.cyan, fontWeight:700, textTransform:'uppercase' }}>💳 Low AI credit</div>
              <div style={{ fontSize:18, fontWeight:800, marginTop:4 }}>{agencyNotifs.counts.lowCredits}</div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>{agencyNotifs.lowCredits.map(l=>`${l.name} ($${((l.ai_credit_cents||0)/100).toFixed(2)})`).slice(0,3).join(', ')}</div>
            </div>
          )}
        </div>
      </div>
    )}

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:12, marginBottom:18 }}>
      <Stat label="Sub-Accounts" value={accounts.length} sub={`${accounts.filter(a=>a.status==='active').length} active · ${accounts.filter(a=>a.status==='archived').length} archived`} color={G.cyan}/>
      <Stat label="Total MRR" value={fmtUsd(totalMrr)} sub="Across all clients" color={G.green}/>
      <Stat label="Client Revenue" value={fmtUsd(totalRev)} sub="Lifetime collected" color={G.purple}/>
      <Stat label="Avg Health" value={`${Math.round(accounts.reduce((s,a)=>s+(a.health||0),0)/Math.max(1,accounts.length))}%`} sub="Across portfolio" color={G.orange}/>
    </div>

    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, gap:8, flexWrap:'wrap' }}>
      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:G.textMuted, cursor:'pointer' }}>
        <input type="checkbox" checked={showArchived} onChange={e=>setShowArchived(e.target.checked)}/>
        Show archived
      </label>
      <div style={{ display:'flex', gap:8 }}>
        <Btn onClick={async()=>{
          try {
            const r = await api('/api/admin-panel/stripe-connect-selftest');
            const lines = r.checks.map(c => `${c.ok?'✅':'❌'} ${c.name}${c.detail ? '\n   ' + (typeof c.detail === 'string' ? c.detail : JSON.stringify(c.detail)) : ''}`).join('\n\n');
            alert(`Stripe Connect Self-Test — ${r.ok?'ALL PASS':'FAILURES'}\n\n${lines}`);
          } catch(e) { toast(e.message, 'error'); }
        }}>🩺 Stripe Connect Self-Test</Btn>
        <Btn onClick={async()=>{
          try {
            const r = await api('/api/admin-panel/backups/test-restore', { method:'POST' });
            const tableLines = r.tables.map(t => `   ${t.name}: backup=${t.backupRows ?? 'N/A'} · live=${t.liveRows ?? 'N/A'}${t.delta!=null?` (Δ ${t.delta>=0?'+':''}${t.delta})`:''}`).join('\n');
            alert(`Backup Restore Test ${r.ok?'✅':'⚠️'}\n\n${r.conclusion}\n\nSnapshot: ${r.snapshot?.filename}\nIntegrity: ${r.integrity?.match===null?'no sidecar hash':r.integrity.match?'✅ match':'❌ MISMATCH'}\n\nRow counts:\n${tableLines}`);
          } catch(e) { toast(e.message, 'error'); }
        }}>💾 Test Backup Restore</Btn>
        <Btn onClick={()=>setBlasting(true)}>📢 Blast All Clients</Btn>
        <Btn v="primary" onClick={()=>setCreating(true)}>+ Onboard New Client</Btn>
      </div>
    </div>

    <Safe name="AgencyTeamCard"><AgencyTeamCard toast={toast} me={user}/></Safe>
    <Safe name="PlatformFeesCard"><PlatformFeesCard toast={toast}/></Safe>

    {/* Kill switches — pause AI/workflows platform-wide */}
    <Safe name="EATokensCard"><EATokensCard toast={toast}/></Safe>

    <Safe name="KillSwitchesCard"><KillSwitchesCard toast={toast}/></Safe>

    {/* Operations health — auto-refresh every 60s, super-admin view */}
    <Safe name="OperationsHealthCard"><OperationsHealthCard toast={toast}/></Safe>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))', gap:14 }}>
      {displayedAccounts.map(a => (
        <div key={a.id} style={{ ...styles.card, padding:18 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
            <div>
              <strong style={{ fontSize:16 }}>{a.name}</strong>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{a.industry} · {a.ownerName}</div>
            </div>
            <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
              <Badge c={G.purple} bg={G.purpleDim}>{a.plan}</Badge>
              <Badge c={a.health>=80?G.green:a.health>=50?G.yellow:G.red} bg={(a.health>=80?G.green:a.health>=50?G.yellow:G.red)+'22'}>
                <Dot c={a.health>=80?G.green:a.health>=50?G.yellow:G.red}/>{a.health}%
              </Badge>
              {a.stripeChargesEnabled
                ? <Badge c={G.green} bg={G.green+'22'} title="Stripe Connect active">💳 Stripe</Badge>
                : a.stripeConnected
                  ? <Badge c={G.yellow} bg={G.yellow+'22'} title="Stripe started but not finished">💳 Pending</Badge>
                  : <Badge c={G.red} bg={G.red+'22'} title="No Stripe Connect setup">💳 Off</Badge>}
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:14, fontSize:12 }}>
            <Mini label="Contacts" value={a.stats?.contacts||0}/>
            <Mini label="Jobs" value={a.stats?.jobs||0}/>
            <Mini label="Team" value={a.stats?.teamSize||0}/>
            <Mini label="MRR" value={fmtUsd(a.mrr||0)} small/>
            <Mini label="Collected" value={fmtUsd(a.stats?.collected||0)} small/>
            <Mini label="Workflows" value={a.stats?.activeWorkflows||0}/>
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', rowGap:6 }}>
            <Btn v="primary" sz="sm" style={{ flex:'1 1 100%' }} onClick={()=>enterAccount(a)}>Enter →</Btn>
            <Btn sz="sm" v="purple" title="Sign in AS the owner of this account — for support walkthroughs" onClick={async()=>{
              try {
                // Find the owner user of this sub-account
                const r = await api(`/api/agency/accounts/${a.id}/owner`);
                if (!r.userId) { toast('No owner user found for this account', 'error'); return; }
                if (!confirm(`Jump in as ${r.firstName||r.email} (owner of ${a.name})?\n\nYou'll see exactly what they see. Click "Exit" in the top bar when done to return to your super-admin session.`)) return;
                await api('/api/auth/jump-user', { method:'POST', body:{ userId: r.userId }});
                window.location.reload();
              } catch(e) { toast(e.message, 'error'); }
            }}>👁 View as Owner</Btn>
            <Btn sz="sm" onClick={()=>setCrmFor(a)}>📋 CRM</Btn>
            <Btn sz="sm" onClick={()=>setFeaturesFor(a)}>🔧 Features</Btn>
            {!a.stripeChargesEnabled && (
              <Btn sz="sm" v="cyan" title="Generate a Stripe Connect onboarding link. Choose to copy or email it to the owner." onClick={async()=>{
                const action = window.prompt(`Stripe Connect for "${a.name}"\n\nType C to copy the link (hand off manually)\nType E to email it to the owner\n\n(C/E):`, 'E');
                if (!action) return;
                const wantsEmail = action.trim().toUpperCase().startsWith('E');
                try {
                  const r = await api(`/api/agency/accounts/${a.id}/stripe-connect-link`, { method:'POST', body:{ email: wantsEmail }});
                  if (r.emailed) {
                    toast(`✓ Emailed Connect link to ${r.to}`);
                  } else if (r.url) {
                    try { await navigator.clipboard.writeText(r.url); toast('✓ Copied Connect link to clipboard'); }
                    catch { window.prompt('Copy this Stripe Connect link:', r.url); }
                  }
                  load();
                } catch(e) { toast(e.message, 'error'); }
              }}>💳 Connect</Btn>
            )}
            <Btn sz="sm" v="cyan" onClick={async()=>{
              try {
                const d = await api(`/api/agency/accounts/${a.id}/diagnose`);
                const inv = d.invoicesByStatus.map(s => `${s.status}:${s.n}`).join(', ') || 'none';
                alert(`📊 ${d.account.name}\n\nContacts: ${d.counts.contacts}\nInvoices: ${d.counts.invoices} (${inv})\nJobs: ${d.counts.jobs}\nExpenses: ${d.counts.expenses}\nTechs: ${d.counts.techs}\nUsers: ${d.counts.users}\n\nAR (sent+overdue): ${d.ar.n} invoices, $${Math.round(d.ar.outstanding).toLocaleString()} outstanding\n\nLast invoice: ${d.recent.lastInvoice[0] ? `${d.recent.lastInvoice[0].num} ${d.recent.lastInvoice[0].contact} ${d.recent.lastInvoice[0].status}` : 'none'}\nLast expense: ${d.recent.lastExpense[0] ? `${d.recent.lastExpense[0].vendor} $${d.recent.lastExpense[0].amount}` : 'none'}`);
              } catch(e) { toast(e.message, 'error'); }
            }}>🔍 Diagnose</Btn>
            <Btn sz="sm" v="cyan" onClick={async()=>{
              try {
                const p = await api(`/api/admin-panel/preview-demo-purge?accountId=${a.id}`);
                const wd = p.wouldDelete, wk = p.wouldKeep;
                const totalDemo = wd.contacts + wd.jobs + wd.invoices + wd.estimates;
                const totalReal = wk.contacts + wk.jobs + wk.invoices + wk.estimates;
                if (totalDemo === 0) return alert(`✅ ${p.account.name} has NO demo data to purge.\n\nReal data preserved (untouched):\n  Contacts: ${wk.contacts}\n  Jobs: ${wk.jobs}\n  Invoices: ${wk.invoices}\n  Estimates: ${wk.estimates}\n\n(Demo already purged: ${p.account.demoAlreadyPurged ? 'YES' : 'no'})`);
                const sample = (p.sampleDemoContacts||[]).map(c => `  · ${c.first_name||''} ${c.last_name||''} · ${c.phone||c.email||'—'} · ${c.source||'?'}`).join('\n');
                const proceed = confirm(
                  `🧪 PURGE DEMO DATA for "${p.account.name}"\n\n` +
                  `WOULD DELETE (all tagged is_demo=1):\n` +
                  `  Contacts: ${wd.contacts}\n  Jobs: ${wd.jobs}\n  Invoices: ${wd.invoices}\n  Estimates: ${wd.estimates}\n\n` +
                  `WOULD KEEP (all real imports untouched):\n` +
                  `  Contacts: ${wk.contacts}\n  Jobs: ${wk.jobs}\n  Invoices: ${wk.invoices}\n  Estimates: ${wk.estimates}\n\n` +
                  `Sample of demo contacts to delete:\n${sample || '  (none)'}\n\n` +
                  `Proceed?`
                );
                if (!proceed) return toast('Cancelled');
                const finalConfirm = prompt('Type exactly:  PURGE_DEMO_ONLY');
                if (finalConfirm !== 'PURGE_DEMO_ONLY') return toast('Cancelled — confirmation phrase did not match');
                const r = await api('/api/admin-panel/purge-demo', { method:'POST', body:{ accountId: a.id, confirm:'PURGE_DEMO_ONLY' }});
                alert(`✅ ${r.accountName}\n\nDeleted (is_demo=1 only):\n  Contacts: ${r.deleted.contacts}\n  Jobs: ${r.deleted.jobs}\n  Invoices: ${r.deleted.invoices}\n  Estimates: ${r.deleted.estimates}\n\nAll real imports untouched.`);
                load();
              } catch(e) { toast(e.message, 'error'); }
            }}>🧪 Purge Demo Only</Btn>
            {a.status === 'archived' ? (
              <Btn sz="sm" v="primary" onClick={async()=>{
                try { await api(`/api/agency/accounts/${a.id}/unarchive`, { method:'POST' }); load(); toast(`Restored ${a.name}`); }
                catch(e) { toast(e.message,'error'); }
              }}>↶ Restore</Btn>
            ) : (
              <Btn sz="sm" onClick={async()=>{
                if (!confirm(`Archive "${a.name}"? Hides from main list + freezes scheduled tasks. Reversible.`)) return;
                try { await api(`/api/agency/accounts/${a.id}/archive`, { method:'POST' }); load(); toast(`Archived ${a.name}`); }
                catch(e) { toast(e.message,'error'); }
              }}>📦 Archive</Btn>
            )}
            <Btn sz="sm" v="danger" onClick={async()=>{
              const name = prompt(`⚠ PERMANENT DELETE of "${a.name}" — all data wiped, irreversible.\n\nType the company name to confirm:`);
              if (name !== a.name) { if (name !== null) toast('Name did not match — delete cancelled', 'warn'); return; }
              try {
                const r = await api(`/api/agency/accounts/${a.id}?confirm=DELETE`, { method:'DELETE' });
                load();
                toast(`✗ Deleted ${r.accountName} · ${r.deletedRows} rows removed`);
              } catch(e) { toast(e.message,'error'); }
            }}>🗑</Btn>
          </div>
        </div>
      ))}
      {accounts.length === 0 && <div style={{ ...styles.card, padding:32, color:G.textMuted, textAlign:'center', gridColumn:'1/-1' }}>No sub-accounts yet. Click "Onboard New Client" to get started.</div>}
    </div>

    <CreateAccountModal open={creating} onClose={()=>setCreating(false)} onCreated={()=>{ load(); setCreating(false); toast('Account created'); }}/>
    <FeaturesModal account={featuresFor} onClose={()=>setFeaturesFor(null)} toast={toast}/>
    <ClientCrmModal account={crmFor} onClose={()=>setCrmFor(null)} onChanged={load} toast={toast}/>
    {blasting && <BlastModal onClose={()=>setBlasting(false)} onSent={(n)=>{ setBlasting(false); toast(`📢 Sent to ${n} clients`); }} toast={toast}/>}
  </>;
}

// Per-account pricing override. Edit the customer price for this client only
// — useful for VIP discounts, problem-client surcharges, custom deals.
// Effective price resolves: account override → global override → default.
// Mandatory Service Call Forms per sub-account. Attaches the EM Electrical
// preset (the PDF Kyle sent) or shows the current attached forms + completion
// rate. Forms marked required_before_close block the tech from advancing a
// job to "Completed" until every required field is filled.
// Apply a full vertical pack (industry + terminology + defaults +
// diagnostic checklist + job categories + booking services + AI persona) to
// a sub-account. One click to "make this account fully Electrical" etc.
function VerticalizeTab({ account, toast, onChanged }) {
  const [industry, setIndustry] = React.useState(account.industry || 'Electrical');
  const [busy, setBusy] = React.useState(false);
  const [reviewer, setReviewer] = React.useState(null); // { current, users }
  const [savingReviewer, setSavingReviewer] = React.useState(false);
  const OPTIONS = ['Electrical','HVAC','Plumbing','Roofing','Landscaping','PestControl','Cleaning','GarageDoor','Pool','Locksmith','Painting','JunkRemoval','Solar','ApplianceRepair','General'];

  React.useEffect(() => {
    api(`/api/agency/accounts/${account.id}/form-reviewer`).then(setReviewer).catch(()=>{});
  }, [account.id]);
  const saveReviewer = async (userId) => {
    setSavingReviewer(true);
    try {
      await api(`/api/agency/accounts/${account.id}/form-reviewer`, { method:'POST', body:{ userId: userId || null }});
      setReviewer(r => ({ ...r, current: userId || null }));
      toast('✓ Reviewer saved — completed forms will notify them in #service-calls', 'success');
    } catch (e) { toast(e.message, 'error'); }
    finally { setSavingReviewer(false); }
  };
  const apply = async () => {
    if (!confirm(`Set ${account.name} to the ${industry} vertical?\n\n• Terminology (worker → tech / stylist / etc.)\n• Job categories + booking services\n• Diagnostic checklist\n• Labor rate / diagnostic fee defaults\n• AI agent voice + persona\n• Electrical Service Call form (if Electrical)\n\nExisting owner-set values are preserved. Safe to run repeatedly.`)) return;
    setBusy(true);
    try {
      const r = await api(`/api/agency/accounts/${account.id}/verticalize`, { method:'POST', body:{ industry }});
      toast(`✓ ${account.name} verticalized as ${r.industry}`, 'success');
      onChanged?.();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <div style={{ padding:'0 4px' }}>
    <div style={{ background:G.card, border:`1px solid ${G.cardBorder}`, borderRadius:12, padding:16, marginBottom:14 }}>
      <div style={{ fontSize:14, fontWeight:800, marginBottom:6 }}>⚡ Verticalize this account</div>
      <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.6, marginBottom:14 }}>
        Applies a trade-specific pack — job categories, diagnostic checklist, labor/diagnostic defaults, booking-widget services, and the AI voice-agent persona. Existing owner-set values (labor rate, custom fields) are preserved.
      </div>
      <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
        <Field label="Industry" style={{ minWidth:220 }}>
          <Select value={industry} onChange={e => setIndustry(e.target.value)}>
            {OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
        </Field>
        <Btn v="primary" onClick={apply} disabled={busy}>{busy ? 'Applying…' : `Apply ${industry} Pack`}</Btn>
      </div>
      <div style={{ marginTop:12, fontSize:12, color:G.textMuted }}>
        Current: <strong style={{ color:G.text }}>{account.industry || 'not set'}</strong>
      </div>
    </div>

    <div style={{ background:G.card, border:`1px solid ${G.cardBorder}`, borderRadius:12, padding:16 }}>
      <div style={{ fontSize:14, fontWeight:800, marginBottom:6 }}>📥 Completed-Form Reviewer</div>
      <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.6, marginBottom:14 }}>
        When a tech finishes a mandatory service-call form, we auto-post a summary to the <strong>#service-calls</strong> team-chat channel and <strong>@mention</strong> this person (in-app + SMS). Pick who should get pinged — usually the office manager or dispatcher.
      </div>
      <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
        <Field label="Reviewer" style={{ minWidth:260 }}>
          <Select value={reviewer?.current || ''} onChange={e => saveReviewer(e.target.value || null)} disabled={savingReviewer || !reviewer}>
            <option value="">— none (skips @mention, still posts) —</option>
            {(reviewer?.users || []).map(u => <option key={u.id} value={u.id}>{u.displayName} · {u.role}</option>)}
          </Select>
        </Field>
        <div style={{ fontSize:11, color:G.textMuted, paddingBottom:10 }}>
          {savingReviewer ? 'Saving…' : reviewer?.current ? '✓ Set' : reviewer?.users?.some(u => u.displayName.toLowerCase().includes('jeremy')) ? 'Not set — but Jeremy will be auto-selected as fallback' : 'Not set'}
        </div>
      </div>
    </div>
  </div>;
}

function CallFormsTab({ accountId, accountName, toast }) {
  const [forms, setForms] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = () => api(`/api/call-forms/for-account/${accountId}`).then(r => setForms(r.forms || [])).catch(()=>setForms([]));
  React.useEffect(() => { load(); }, [accountId]);
  const attachEm = async () => {
    if (!confirm(`Attach the "Electrical Service Call Questions" form to ${accountName}?\n\nEvery tech's job will require it to be filled before they can mark it Completed. Existing forms are kept.`)) return;
    setBusy(true);
    try { await api('/api/call-forms/attach-em-electrical', { method:'POST', body:{ accountId }});
      toast('✓ Form attached'); load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <div>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14, lineHeight:1.55 }}>
      Forms that this sub-account's techs are required to fill on every service call. When a form is marked <strong>required_before_close</strong>, the tech cannot mark the job Completed until every required field has a value. Auto-saves as they type.
    </div>
    {!forms ? <Spinner sz={20}/> : forms.length === 0 ? (
      <div style={{ ...styles.card, padding:16, marginBottom:12 }}>
        <p style={{ margin:0, fontSize:14, color:G.textMuted }}>No forms attached yet.</p>
      </div>
    ) : (
      forms.map(f => <div key={f.id} style={{ ...styles.card, padding:14, marginBottom:10 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
          <div>
            <strong style={{ fontSize:14 }}>{f.name}</strong>
            {f.description && <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{f.description}</div>}
            <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>Key: <code>{f.formKey}</code></div>
          </div>
          <div style={{ textAlign:'right', minWidth:140 }}>
            <div style={{ fontSize:22, fontWeight:800, color: f.stats.pct >= 80 ? G.green : f.stats.pct >= 40 ? G.yellow : G.red }}>{f.stats.pct}%</div>
            <div style={{ fontSize:11, color:G.textMuted }}>{f.stats.complete} / {f.stats.total} completed</div>
          </div>
        </div>
      </div>)
    )}
    <div style={{ display:'flex', gap:8, marginTop:14 }}>
      <Btn v="primary" onClick={attachEm} disabled={busy}>+ Attach "Electrical Service Call Questions" (EM template)</Btn>
    </div>
  </div>;
}

// Price Book bulk import — super-admin only. Paste raw CSV from any
// spreadsheet export; the server parses, wipes the old book (if Replace All),
// and inserts. Case-insensitive header matching so different export formats
// work without pre-cleaning.
function PriceBookImportTab({ accountId, accountName, toast }) {
  const [csv, setCsv] = React.useState('');
  const [replace, setReplace] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const submit = async () => {
    if (!csv.trim()) return toast('Paste the CSV first', 'error');
    if (replace && !confirm(`Replace ${accountName}'s entire price book with the pasted CSV? Existing items will be deleted.`)) return;
    setBusy(true); setResult(null);
    try {
      const r = await api('/api/pricebook/import', { method:'POST', body:{ accountId, csv, replace }});
      setResult(r);
      toast(`✓ Imported ${r.inserted} items${r.wiped?` · wiped ${r.wiped} existing`:''}`);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <div>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:10, lineHeight:1.55 }}>
      Paste raw CSV from any spreadsheet export. First row must be the header. Recognized columns (any order): <code>task_code/sku</code>, <code>name</code>, <code>category</code>, <code>subcategory_1</code>, <code>unit_of_measure/unit</code>, <code>price</code>, <code>cost</code>, <code>taxable</code>, <code>description</code>.
    </div>
    <label style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, cursor:'pointer' }}>
      <input type="checkbox" checked={replace} onChange={e=>setReplace(e.target.checked)}/>
      <span style={{ fontSize:14, fontWeight:600 }}>Replace all existing items (recommended)</span>
    </label>
    <textarea value={csv} onChange={e=>setCsv(e.target.value)}
      placeholder="industry,category,name,description,price,cost,taxable,unit_of_measure,task_code..."
      rows={14}
      style={{ ...styles.inp, width:'100%', fontFamily:'ui-monospace,monospace', fontSize:12 }}/>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:10 }}>
      <Btn onClick={()=>{ setCsv(''); setResult(null); }}>Clear</Btn>
      <Btn v="primary" onClick={submit} disabled={busy || !csv.trim()}>{busy?'Importing…':`📚 Import ${replace?'(Replace)':'(Append)'}`}</Btn>
    </div>
    {result && (
      <div style={{ marginTop:12, ...styles.card, padding:12, background:G.green+'11', border:`1px solid ${G.green}44` }}>
        <div style={{ fontWeight:700, color:G.green, marginBottom:6 }}>✅ Import complete for {result.accountName}</div>
        <div style={{ fontSize:12, color:G.textMuted }}>Before: {result.before} · Wiped: {result.wiped} · Inserted: {result.inserted}</div>
        {result.sample?.length > 0 && (
          <details style={{ marginTop:8 }}>
            <summary style={{ cursor:'pointer', fontSize:12, color:G.cyan }}>Sample of imported items</summary>
            <pre style={{ padding:10, background:G.input, borderRadius:6, fontSize:11, overflow:'auto', maxHeight:200 }}>{JSON.stringify(result.sample, null, 2)}</pre>
          </details>
        )}
      </div>
    )}
  </div>;
}

// Per-account platform-fee toggle + override. Shown at the top of the
// Pricing tab. Override wins over the agency-wide plan default. Toggle off
// = we charge 0% platform fee to this account (VIP / partner deals).
function AccountPlatformFeeCard({ accountId, toast }) {
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [override, setOverride] = React.useState('');
  const load = () => api(`/api/agency/accounts/${accountId}/platform-fee`).then(r => { setState(r); setOverride(r.override_pct != null ? String(r.override_pct) : ''); }).catch(()=>{});
  React.useEffect(() => { load(); }, [accountId]);
  if (!state) return null;
  const toggle = async (enabled) => {
    setBusy(true);
    try { await api(`/api/agency/accounts/${accountId}/platform-fee`, { method:'PATCH', body:{ enabled }}); load(); toast(enabled ? '✓ Platform fee ON' : '✓ Platform fee OFF for this account', 'success'); }
    catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };
  const saveOverride = async () => {
    setBusy(true);
    try { await api(`/api/agency/accounts/${accountId}/platform-fee`, { method:'PATCH', body:{ override_pct: override === '' ? null : Number(override) }}); load(); toast('✓ Override saved', 'success'); }
    catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };
  return <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor: state.enabled ? G.cardBorder : G.red+'55' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, gap:10, flexWrap:'wrap' }}>
      <div>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:800, textTransform:'uppercase', letterSpacing:'.05em' }}>💰 Stripe Platform Fee</div>
        <div style={{ fontSize:20, fontWeight:800, marginTop:4 }}>
          {state.enabled ? `${state.effective_pct}%` : 'OFF (0%)'}
        </div>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>
          Plan {state.plan} · default {state.plan_default_pct}%{state.override_pct != null ? ` · override ${state.override_pct}%` : ''}
        </div>
      </div>
      <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', userSelect:'none' }}>
        <input type="checkbox" checked={state.enabled} onChange={e=>toggle(e.target.checked)} disabled={busy} style={{ width:20, height:20, cursor:'pointer' }}/>
        <span style={{ fontSize:13, fontWeight:700 }}>{state.enabled ? 'Collect fee' : 'No fee'}</span>
      </label>
    </div>
    {state.enabled && <div style={{ display:'flex', gap:8, alignItems:'center' }}>
      <input type="number" step="0.05" min="0" max="15" value={override} onChange={e=>setOverride(e.target.value)}
        placeholder={`Override % (leave blank for plan default ${state.plan_default_pct}%)`}
        style={{ flex:1, padding:'8px 10px', border:`1px solid ${G.border}`, background:G.input, color:G.text, borderRadius:6, fontSize:13 }}/>
      <Btn sz="sm" v="primary" onClick={saveOverride} disabled={busy}>Save</Btn>
    </div>}
  </div>;
}

function AccountPricingTab({ accountId, toast }) {
  const [rows, setRows] = useState(null);
  const load = () => api(`/api/agency/accounts/${accountId}/pricing`).then(d => setRows(d.rateCard)).catch(()=>setRows([]));
  useEffect(() => { load(); }, [accountId]);
  if (!rows) return <Spinner sz={20}/>;

  const save = async (key, customerCents) => {
    try { await api(`/api/agency/accounts/${accountId}/pricing`, { method:'PATCH', body:{ key, customerCents }}); await load(); toast('✓ Saved'); }
    catch (e) { toast(e.message, 'error'); }
  };

  return <div>
    <AccountPlatformFeeCard accountId={accountId} toast={toast}/>

    <div style={{ fontSize:13, color:G.textMuted, marginBottom:10, lineHeight:1.5 }}>
      Per-account pricing override. Affects ONLY this client. Leaves global pricing alone for everyone else. Use for VIP discounts or custom deals.
    </div>
    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead><tr style={{ background:G.input, fontSize:11, color:G.textMuted, textTransform:'uppercase' }}>
          <th style={{ textAlign:'left', padding:'8px 10px' }}>Line</th>
          <th style={{ textAlign:'right', padding:'8px 10px' }}>Default</th>
          <th style={{ textAlign:'right', padding:'8px 10px' }}>Global Ov.</th>
          <th style={{ textAlign:'right', padding:'8px 10px' }}>This Account</th>
          <th style={{ textAlign:'right', padding:'8px 10px' }}>Effective</th>
          <th style={{ textAlign:'left', padding:'8px 10px' }}>Source</th>
          <th></th>
        </tr></thead>
        <tbody>{rows.map(r => <AccountPricingRow key={r.key} row={r} onSave={save}/>)}</tbody>
      </table>
    </div>
  </div>;
}

function AccountPricingRow({ row, onSave }) {
  const [val, setVal] = useState(row.accountOverrideCents != null ? row.accountOverrideCents : '');
  useEffect(()=>{ setVal(row.accountOverrideCents != null ? row.accountOverrideCents : ''); }, [row.accountOverrideCents]);
  const dirty = (val === '' && row.accountOverrideCents != null) || (val !== '' && Number(val) !== row.accountOverrideCents);
  return <tr style={{ borderTop:`1px solid ${G.border}` }}>
    <td style={{ padding:'8px 10px', fontFamily:'monospace', fontSize:11 }}>{row.key}</td>
    <td style={{ padding:'8px 10px', textAlign:'right', color:G.textMuted, fontFamily:'monospace' }}>{row.defaultCents.toFixed(1)}¢</td>
    <td style={{ padding:'8px 10px', textAlign:'right', fontFamily:'monospace' }}>{row.globalOverrideCents != null ? row.globalOverrideCents.toFixed(1)+'¢' : '—'}</td>
    <td style={{ padding:'8px 10px', textAlign:'right' }}>
      <input type="number" step="0.1" value={val} onChange={e=>setVal(e.target.value)} placeholder="—"
        style={{ width:74, padding:4, background:G.input, color:G.text, border:`1px solid ${G.cardBorder}`, borderRadius:4, fontSize:12, fontFamily:'monospace', textAlign:'right' }}/>
    </td>
    <td style={{ padding:'8px 10px', textAlign:'right', fontWeight:700, fontFamily:'monospace', color: row.marginPct >= 30 ? G.green : G.yellow }}>
      {row.effectiveCents.toFixed(1)}¢ · {row.marginPct}%
    </td>
    <td style={{ padding:'8px 10px', fontSize:10, color: row.source==='account'?G.purple : row.source==='global'?G.cyan : G.textMuted, textTransform:'uppercase', fontWeight:700 }}>
      {row.source}
    </td>
    <td style={{ padding:'8px 10px' }}>
      <div style={{ display:'flex', gap:4 }}>
        <Btn sz="xs" v="primary" disabled={!dirty} onClick={()=>onSave(row.key, val === '' ? null : Number(val))}>Save</Btn>
        {row.accountOverrideCents != null && <Btn sz="xs" v="ghost" onClick={()=>onSave(row.key, null)}>Clear</Btn>}
      </div>
    </td>
  </tr>;
}

function BlastModal({ onClose, onSent, toast }) {
  const [body, setBody] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!body.trim()) return toast('Message body required', 'error');
    if (!confirm(`Broadcast this to ALL ${planFilter ? planFilter+' ' : 'active '}sub-accounts? Each one gets a notification + inbox message.`)) return;
    setBusy(true);
    try { const r = await api('/api/agency/blast', { method:'POST', body:{ body, planFilter: planFilter||undefined }}); onSent(r.sent); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={560}>
    <h2 style={{ margin:'0 0 6px', fontSize:18 }}>📢 Blast All Clients</h2>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
      Drops a message into every active sub-account's inbox + bell. Use for product updates, maintenance windows, new features.
    </div>
    <Field label="Limit to plan (optional)">
      <Select value={planFilter} onChange={e=>setPlanFilter(e.target.value)}>
        <option value="">All active accounts</option>
        <option value="Starter">Starter only</option>
        <option value="Pro">Pro only</option>
        <option value="Elite">Elite only</option>
      </Select>
    </Field>
    <Field label="Message">
      <textarea value={body} onChange={e=>setBody(e.target.value)} rows={5}
        placeholder="Hey team — quick update on the new Content Studio. Schedule + AI Drip are live this week..."
        style={{ width:'100%', padding:10, background:G.input, color:G.text, border:`1px solid ${G.cardBorder}`, borderRadius:8, fontSize:13, fontFamily:'inherit', resize:'vertical' }}/>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
      <Btn v="primary" onClick={send} disabled={busy || !body.trim()}>{busy ? <Spinner sz={14}/> : '📢 Send Blast'}</Btn>
    </div>
  </Modal>;
}

// Super-admin mini-CRM for one sub-account. Tabs:
//   Notes   — sales / onboarding / support notes the agency keeps
//   Billing — change plan, MRR, threshold, subscription status, trial end
//   Margin  — what we pay vendors vs what we billed this client this month
function ClientCrmModal({ account, onClose, onChanged, toast }) {
  const [tab, setTab] = useState('notes');
  const [notes, setNotes] = useState(null);
  const [margin, setMargin] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [editing, setEditing] = useState(null); // editable account fields
  const [messages, setMessages] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [tasks, setTasks] = useState(null);
  const [newTask, setNewTask] = useState({ title:'', dueAt:'' });
  const [health, setHealth] = useState(null);

  useEffect(() => {
    if (!account) return;
    setEditing({
      plan: account.plan || 'Starter', mrr: account.mrr || 0,
      subscription_status: account.subscriptionStatus || account.subscription_status || 'active',
      trial_ends_at: account.trialEndsAt || account.trial_ends_at || '',
      usage_threshold_cents: account.usageThresholdCents || account.usage_threshold_cents || 5000,
      billing_authorized: !!account.billing_authorized,
    });
    api(`/api/agency/accounts/${account.id}/notes`).then(setNotes).catch(()=>setNotes([]));
    api(`/api/agency/accounts/${account.id}/margin`).then(setMargin).catch(()=>setMargin({ error: true }));
    api(`/api/agency/accounts/${account.id}/messages`).then(setMessages).catch(()=>setMessages([]));
    api(`/api/agency/accounts/${account.id}/tasks`).then(setTasks).catch(()=>setTasks([]));
    api(`/api/agency/accounts/${account.id}/health`).then(setHealth).catch(()=>{});
  }, [account?.id]);

  if (!account) return null;

  const addNote = async () => {
    if (!newNote.trim()) return;
    try { await api(`/api/agency/accounts/${account.id}/notes`, { method:'POST', body:{ body: newNote }}); setNewNote(''); api(`/api/agency/accounts/${account.id}/notes`).then(setNotes); toast('Note added'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const togglePin = async (n) => { try { await api(`/api/agency/notes/${n.id}`, { method:'PATCH', body:{ pinned: !n.pinned }}); api(`/api/agency/accounts/${account.id}/notes`).then(setNotes); } catch (e) { toast(e.message,'error'); } };
  const delNote   = async (n) => { if (!confirm('Delete note?')) return; try { await api(`/api/agency/notes/${n.id}`, { method:'DELETE' }); api(`/api/agency/accounts/${account.id}/notes`).then(setNotes); } catch (e) { toast(e.message,'error'); } };
  const saveBilling = async () => {
    try { await api(`/api/agency/accounts/${account.id}`, { method:'PATCH', body: editing }); onChanged(); toast('Saved'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const saveCustomPricing = async () => {
    try {
      await api(`/api/admin-panel/custom-pricing/${account.id}`, { method:'PATCH', body:{
        customMonthlyPriceCents: editing.custom_monthly_price_cents == null || editing.custom_monthly_price_cents === '' ? null : Math.round(Number(editing.custom_monthly_price_cents)),
        subscriptionCancelAt: editing.subscription_cancel_at || null,
      }});
      onChanged(); toast('✓ Custom pricing saved');
    } catch (e) { toast(e.message, 'error'); }
  };
  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await api(`/api/agency/accounts/${account.id}/messages`, { method:'POST', body:{ body: newMessage }});
      setNewMessage('');
      api(`/api/agency/accounts/${account.id}/messages`).then(setMessages);
      toast('✓ Sent — owner notified');
    } catch (e) { toast(e.message, 'error'); }
  };
  const addTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      await api(`/api/agency/accounts/${account.id}/tasks`, { method:'POST', body: newTask });
      setNewTask({ title:'', dueAt:'' });
      api(`/api/agency/accounts/${account.id}/tasks`).then(setTasks);
    } catch (e) { toast(e.message, 'error'); }
  };
  const toggleTask = async (t) => {
    try {
      await api(`/api/agency/tasks/${t.id}`, { method:'PATCH', body:{ status: t.status==='done'?'open':'done' }});
      api(`/api/agency/accounts/${account.id}/tasks`).then(setTasks);
    } catch (e) { toast(e.message, 'error'); }
  };
  const delTask = async (t) => {
    try { await api(`/api/agency/tasks/${t.id}`, { method:'DELETE' }); api(`/api/agency/accounts/${account.id}/tasks`).then(setTasks); } catch (e) { toast(e.message, 'error'); }
  };

  return <Modal open onClose={onClose} width={720}>
    <h2 style={{ margin:'0 0 6px', fontSize:18 }}>📋 {account.name}</h2>
    <div style={{ fontSize:12, color:G.textMuted, marginBottom:14 }}>
      {account.industry||'—'} · {account.email||account.ownerName||'no contact'} · Plan: {account.plan}
    </div>

    <div style={{ display:'flex', gap:4, marginBottom:14, borderBottom:`1px solid ${G.cardBorder}`, overflowX:'auto' }}>
      {[
        ['vertical', '⚡ Vertical Setup'],
        ['notes',    '📝 Notes'],
        ['messages', `💬 Messages${messages?.filter(m=>m.direction==='from_owner'&&!m.read_at).length?` (${messages.filter(m=>m.direction==='from_owner'&&!m.read_at).length})`:''}`],
        ['tasks',    `✅ Tasks${tasks?.filter(t=>t.status==='open').length?` (${tasks.filter(t=>t.status==='open').length})`:''}`],
        ['health',   `❤️ Health${health?` · ${health.score}`:''}`],
        ['billing',  '💳 Billing'],
        ['pricing',  '💰 Pricing'],
        ['pricebook','📚 Price Book'],
        ['callforms','🚫 Call Forms'],
        ['margin',   '📊 Margin'],
      ].map(([k,l]) => (
        <button key={k} onClick={()=>setTab(k)} style={{ background:'none', border:'none', color: tab===k?G.purple:G.textMuted, padding:'8px 14px', fontWeight: tab===k?700:500, cursor:'pointer', borderBottom: tab===k?`2px solid ${G.purple}`:'2px solid transparent', marginBottom:-1, whiteSpace:'nowrap' }}>{l}</button>
      ))}
    </div>

    {tab === 'notes' && <div>
      <div style={{ display:'flex', gap:6, marginBottom:12 }}>
        <Input value={newNote} onChange={e=>setNewNote(e.target.value)} placeholder="Quick note — call summary, blocker, next action…" onKeyDown={e=>e.key==='Enter' && addNote()}/>
        <Btn v="primary" onClick={addNote} disabled={!newNote.trim()}>Add</Btn>
      </div>
      {!notes ? <Spinner sz={20}/> : notes.length === 0 ? <div style={{ color:G.textMuted, fontSize:13, padding:20, textAlign:'center' }}>No notes yet.</div> :
        notes.map(n => (
          <div key={n.id} style={{ ...styles.card, padding:12, marginBottom:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div style={{ flex:1, minWidth:0, fontSize:13, whiteSpace:'pre-wrap' }}>
                {n.pinned && '📌 '}{n.body}
              </div>
              <div style={{ display:'flex', gap:4 }}>
                <Btn sz="xs" onClick={()=>togglePin(n)}>{n.pinned?'Unpin':'Pin'}</Btn>
                <Btn sz="xs" v="danger" onClick={()=>delNote(n)}>×</Btn>
              </div>
            </div>
            <div style={{ fontSize:11, color:G.textMuted, marginTop:6 }}>{n.authorEmail||'?'} · {new Date(n.createdAt).toLocaleString()}</div>
          </div>
        ))
      }
    </div>}

    {tab === 'messages' && <div>
      <div style={{ ...styles.card, padding:0, maxHeight:380, overflowY:'auto', marginBottom:10 }}>
        {!messages ? <Spinner sz={20}/> : messages.length === 0
          ? <div style={{ padding:24, color:G.textMuted, fontSize:13, textAlign:'center' }}>No messages yet. Say hi 👋</div>
          : messages.slice().reverse().map(m => (
            <div key={m.id} style={{ padding:'10px 14px', borderBottom:`1px solid ${G.border}`, background: m.direction === 'from_owner' ? G.input : 'transparent' }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:G.textMuted, marginBottom:4 }}>
                <strong style={{ color: m.direction === 'from_owner' ? G.cyan : G.purple }}>{m.direction === 'from_owner' ? '← Owner' : '→ Agency'}</strong>
                <span>{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <div style={{ fontSize:13, whiteSpace:'pre-wrap' }}>{m.body}</div>
            </div>
          ))}
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <input value={newMessage} onChange={e=>setNewMessage(e.target.value)} onKeyDown={e=>e.key==='Enter' && !e.shiftKey && sendMessage()}
          placeholder="Message the owner — they'll see this in their inbox + bell"
          style={{ flex:1, padding:10, background:G.input, color:G.text, border:`1px solid ${G.cardBorder}`, borderRadius:8, fontSize:13 }}/>
        <Btn v="primary" onClick={sendMessage} disabled={!newMessage.trim()}>Send</Btn>
      </div>
    </div>}

    {tab === 'tasks' && <div>
      <div style={{ ...styles.card, padding:12, marginBottom:10, background:G.input }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr auto', gap:8 }}>
          <input value={newTask.title} onChange={e=>setNewTask({...newTask, title:e.target.value})} placeholder="Title — e.g. 'Follow up about Q3 expansion'"
            style={{ padding:8, background:G.bg, color:G.text, border:`1px solid ${G.cardBorder}`, borderRadius:6, fontSize:13 }}/>
          <input type="date" value={newTask.dueAt} onChange={e=>setNewTask({...newTask, dueAt:e.target.value})}
            style={{ padding:8, background:G.bg, color:G.text, border:`1px solid ${G.cardBorder}`, borderRadius:6, fontSize:13 }}/>
          <Btn v="primary" onClick={addTask} disabled={!newTask.title.trim()}>Add</Btn>
        </div>
      </div>
      {!tasks ? <Spinner sz={20}/> : tasks.length === 0
        ? <div style={{ color:G.textMuted, fontSize:13, padding:20, textAlign:'center' }}>No tasks yet.</div>
        : tasks.map(t => {
          const overdue = t.status === 'open' && t.due_at && t.due_at < new Date().toISOString().slice(0,10);
          return <div key={t.id} style={{ ...styles.card, padding:12, marginBottom:8, opacity: t.status==='done'?0.5:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input type="checkbox" checked={t.status==='done'} onChange={()=>toggleTask(t)}/>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600, textDecoration: t.status==='done'?'line-through':'none' }}>{t.title}</div>
                {t.due_at && <div style={{ fontSize:11, color: overdue?G.red:G.textMuted, marginTop:2 }}>{overdue?'⚠ Overdue · ':''}Due {t.due_at}</div>}
              </div>
              <Btn sz="xs" v="danger" onClick={()=>delTask(t)}>×</Btn>
            </div>
          </div>;
        })}
    </div>}

    {tab === 'health' && health && <div>
      <div style={{ ...styles.card, padding:20, marginBottom:14, textAlign:'center', borderColor:(health.score>=60?G.green:health.score>=30?G.yellow:G.red)+'66' }}>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Health Score</div>
        <div style={{ fontSize:56, fontWeight:800, color: health.score>=60?G.green:health.score>=30?G.yellow:G.red }}>{health.score}</div>
        <div style={{ fontSize:13, color: health.churnRisk==='high'?G.red:health.churnRisk==='medium'?G.yellow:G.green, fontWeight:600 }}>
          Churn risk: {health.churnRisk}
        </div>
      </div>
      <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Why</div>
      {health.reasons.map((r, i) => (
        <div key={i} style={{ fontSize:13, padding:'6px 0', borderBottom:`1px solid ${G.border}` }}>{r}</div>
      ))}
    </div>}

    {tab === 'billing' && editing && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Plan">
        <Select value={editing.plan} onChange={e=>setEditing({...editing, plan:e.target.value})}>
          {['Starter','Pro','Elite'].map(p => <option key={p}>{p}</option>)}
        </Select>
      </Field>
      <Field label="Monthly Revenue ($)">
        <Input type="number" value={editing.mrr} onChange={e=>setEditing({...editing, mrr:Number(e.target.value)})}/>
      </Field>
      <Field label="Subscription status">
        <Select value={editing.subscription_status} onChange={e=>setEditing({...editing, subscription_status:e.target.value})}>
          {['trialing','active','past_due','canceled','paused'].map(s => <option key={s}>{s}</option>)}
        </Select>
      </Field>
      <Field label="Trial ends at">
        <Input type="date" value={(editing.trial_ends_at||'').slice(0,10)} onChange={e=>setEditing({...editing, trial_ends_at:e.target.value})}/>
      </Field>
      <Field label="Usage threshold ($)">
        <Input type="number" value={(editing.usage_threshold_cents||0)/100} onChange={e=>setEditing({...editing, usage_threshold_cents:Math.round(Number(e.target.value)*100)})}/>
      </Field>
      <Field label="Billing authorized" style={{ display:'flex', alignItems:'center', gap:6, paddingTop:24 }}>
        <input type="checkbox" checked={!!editing.billing_authorized} onChange={e=>setEditing({...editing, billing_authorized:e.target.checked ? 1 : 0})}/>
        <span style={{ fontSize:13 }}>{editing.billing_authorized ? 'Authorized' : 'Not authorized'}</span>
      </Field>
      <div style={{ gridColumn:'1/-1', display:'flex', justifyContent:'flex-end', gap:10 }}>
        <Btn v="primary" onClick={saveBilling}>Save Billing</Btn>
      </div>

      {/* Custom SaaS pricing — grandfather deals (e.g. Electric Medic $497/mo capped at 12 mo) */}
      <div style={{ gridColumn:'1/-1', marginTop:14, padding:14, background:G.purpleDim, border:`1px solid ${G.purpleBorder}`, borderRadius:10 }}>
        <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>🎯 Custom SaaS Pricing (Grandfather Deal)</div>
        <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>
          Overrides plan price for this account. Set a cancel date and billing auto-stops on that date —
          the account keeps full access afterward (paid-up forever).
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <Field label="Custom monthly price ($) — blank uses plan default">
            <Input type="number" value={editing.custom_monthly_price_cents == null ? '' : (editing.custom_monthly_price_cents/100)}
              onChange={e=>setEditing({...editing, custom_monthly_price_cents: e.target.value === '' ? null : Math.round(Number(e.target.value)*100)})}
              placeholder="e.g. 497"/>
          </Field>
          <Field label="Stop billing on (date) — blank = no cap">
            <Input type="date" value={(editing.subscription_cancel_at||'').slice(0,10)}
              onChange={e=>setEditing({...editing, subscription_cancel_at: e.target.value || null})}/>
          </Field>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:10 }}>
          <Btn v="primary" onClick={saveCustomPricing}>Save Custom Pricing</Btn>
        </div>
      </div>

      {/* Installment fee deal — for setups like Electric Medic ($500/wk × 12 → $6k → $497/mo) */}
      <div style={{ gridColumn:'1/-1', marginTop:14 }}>
        <Safe name="InstallmentPlanCard"><InstallmentPlanCard accountId={account.id} toast={toast}/></Safe>
      </div>
    </div>}

    {tab === 'pricing' && <AccountPricingTab accountId={account.id} toast={toast}/>}
    {tab === 'pricebook' && <PriceBookImportTab accountId={account.id} accountName={account.name} toast={toast}/>}
    {tab === 'callforms' && <CallFormsTab accountId={account.id} accountName={account.name} toast={toast}/>}
    {tab === 'vertical' && <VerticalizeTab account={account} toast={toast} onChanged={onChanged}/>}
    {tab === 'margin' && <div>
      {margin === null ? <Spinner sz={20}/> :
       !margin?.monthToDate ? (
         <div style={{ color:G.textMuted, fontSize:13, padding:14 }}>
           No margin data yet — this account has no metered usage this month, or the margin service isn't returning MTD rollups.
         </div>
       ) : <>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10, marginBottom:14 }}>
          <Mini label="Billed (MTD)" value={fmtUsd(margin.monthToDate.billedUsd || 0)} small/>
          <Mini label="Our cost (MTD)" value={fmtUsd(margin.monthToDate.ourCostUsd || 0)} small/>
          <Mini label="Margin $ (MTD)" value={fmtUsd(margin.monthToDate.marginUsd || 0)} small/>
          <Mini label="Margin %" value={`${margin.monthToDate.marginPct ?? 0}%`} small/>
        </div>
        <div style={{ fontSize:11, color:G.textMuted, marginBottom:8, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>By kind</div>
        {!(margin.monthToDate.breakdown || []).length ? <div style={{ color:G.textMuted, fontSize:13, padding:14 }}>No metered usage this month.</div> :
          <table style={{ width:'100%', fontSize:13 }}>
            <thead><tr style={{ color:G.textMuted, fontSize:11, textTransform:'uppercase' }}>
              <th style={{ textAlign:'left', padding:'6px 8px' }}>Kind / Model</th>
              <th style={{ textAlign:'right', padding:'6px 8px' }}># Used</th>
              <th style={{ textAlign:'right', padding:'6px 8px' }}>Our Cost</th>
              <th style={{ textAlign:'right', padding:'6px 8px' }}>Billed</th>
              <th style={{ textAlign:'right', padding:'6px 8px' }}>Margin</th>
            </tr></thead>
            <tbody>{(margin.monthToDate.breakdown || []).map((b, i) => (
              <tr key={i} style={{ borderTop:`1px solid ${G.border}` }}>
                <td style={{ padding:'6px 8px' }}>{b.kind} · {b.model||'—'}</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{b.count}</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmtUsd(b.ourCostUsd)}</td>
                <td style={{ padding:'6px 8px', textAlign:'right' }}>{fmtUsd(b.billedUsd)}</td>
                <td style={{ padding:'6px 8px', textAlign:'right', color: b.marginUsd>=0?G.green:G.red, fontWeight:600 }}>{fmtUsd(b.marginUsd)}</td>
              </tr>
            ))}</tbody>
          </table>}
      </>}
    </div>}
  </Modal>;
}

function FeaturesModal({ account, onClose, toast }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (account) api(`/api/admin-panel/features/${account.id}`).then(setData).catch(()=>{}); }, [account]);
  if (!account) return null;

  const FEATURE_LABELS = {
    dashboard:'Dashboard', financials:'Financials', estimates:'Estimates', invoices:'Invoices',
    jobs:'Dispatch', agents:'AI Agents', pipeline:'Pipeline', calendar:'Calendar',
    marketing:'Marketing', workflows:'Workflows', forms:'Forms', memberships:'Memberships',
    messaging:'Messages', contacts:'Contacts', growth:'Growth + Revenue',
    pricebook:'Price Book', permits:'Permits', documents:'Documents', equipment:'Equipment',
    inventory:'Inventory', timesheets:'Timesheets', team:'Team', integrations:'Integrations',
    ai_voice:'AI Voice agents', ai_chat:'AI SMS chat', marketplace:'Marketplace',
    financing:'Customer financing', reputation:'Reputation mgmt', whitelabel:'White-label',
    predictive:'Predictive maintenance',
  };

  const toggle = async (key, enabled) => {
    try {
      await api(`/api/admin-panel/features/${account.id}`, { method:'PATCH', body:{ updates: { [key]: enabled }}});
      const fresh = await api(`/api/admin-panel/features/${account.id}`);
      setData(fresh);
      toast(`${FEATURE_LABELS[key]||key}: ${enabled?'on':'off'}`);
    } catch(e) { toast(e.message,'error'); }
  };

  if (!data) return <Modal open onClose={onClose}><Spinner sz={32}/></Modal>;

  return <Modal open onClose={onClose} width={680}>
    <h2 style={{ margin:'0 0 6px 0' }}>🔧 Feature Flags — {account.name}</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>Disable any feature to hide it from this account's sidebar. Defaults to all-on.</div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:6 }}>
      {data.all.map(k => {
        const on = data.effective[k];
        return <label key={k} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background: on?G.greenLight:G.input, border:`1px solid ${on?G.green+'66':G.cardBorder}`, borderRadius:8, cursor:'pointer' }}>
          <input type="checkbox" checked={!!on} onChange={e=>toggle(k, e.target.checked)}/>
          <span style={{ fontSize:13 }}>{FEATURE_LABELS[k]||k}</span>
        </label>;
      })}
    </div>
    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}><Btn onClick={onClose}>Close</Btn></div>
  </Modal>;
}

function CreateAccountModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ industry:'HVAC', plan:'Starter', mrr:297 });
  const [tempPw, setTempPw] = useState('');
  if (!open) return null;
  const submit = async () => {
    if (!form.businessName || !form.email) return alert('Business name and email required');
    try { const r = await api('/api/agency/accounts', { method:'POST', body: form }); setTempPw(r.tempPassword); }
    catch(e) { alert(e.message); }
  };
  if (tempPw) {
    return <Modal open onClose={onCreated} width={460}>
      <h2 style={{ margin:'0 0 8px 0' }}>✅ Account Created</h2>
      <div style={{ color:G.textMuted, marginBottom:18 }}>Send these credentials to the new client.</div>
      <div style={{ ...styles.card, background:G.input, padding:18 }}>
        <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Login</div>
        <div style={{ fontSize:14, marginBottom:8 }}>{form.email}</div>
        <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Temporary Password</div>
        <div style={{ fontSize:22, fontWeight:800, fontFamily:'monospace', color:G.green }}>{tempPw}</div>
      </div>
      <Btn v="primary" full style={{ marginTop:14 }} onClick={onCreated}>Got it</Btn>
    </Modal>;
  }
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 14px 0' }}>Onboard New Client</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Business Name *"><Input value={form.businessName||''} onChange={e=>setForm({...form,businessName:e.target.value})}/></Field>
      <Field label="Industry"><Select value={form.industry||'HVAC'} onChange={e=>setForm({...form,industry:e.target.value})}>{['HVAC','Plumbing','Electrical','Roofing','General Contractor','Landscaping','Cleaning'].map(i => <option key={i}>{i}</option>)}</Select></Field>
      <Field label="Owner Name"><Input value={form.ownerName||''} onChange={e=>setForm({...form,ownerName:e.target.value})}/></Field>
      <Field label="Owner Email *"><Input type="email" value={form.email||''} onChange={e=>setForm({...form,email:e.target.value})}/></Field>
      <Field label="Phone"><Input value={form.phone||''} onChange={e=>setForm({...form,phone:e.target.value})}/></Field>
      <Field label="Plan"><Select value={form.plan||'Starter'} onChange={e=>setForm({...form,plan:e.target.value})}><option>Starter</option><option>Pro</option><option>Elite</option></Select></Field>
      <Field label="MRR ($)"><Input type="number" value={form.mrr||0} onChange={e=>setForm({...form,mrr:Number(e.target.value)})}/></Field>
      <Field label="Owner Password (auto if blank)"><Input value={form.ownerPassword||''} onChange={e=>setForm({...form,ownerPassword:e.target.value})}/></Field>
    </div>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={submit}>Create</Btn>
    </div>
  </Modal>;
}

function SnapshotsTab({ toast }) {
  const [snapshots, setSnapshots] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capture, setCapture] = useState(false);

  const load = () => Promise.all([api('/api/agency/snapshots'), api('/api/agency/accounts')])
    .then(([s,a]) => { setSnapshots(s); setAccounts(a); })
    .catch(e=>toast(e.message,'error'))
    .finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async s => {
    if (!confirm(`Delete snapshot "${s.name}"?`)) return;
    try { await api(`/api/agency/snapshots/${s.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <Spinner/>;

  return <>
    <div style={{ ...styles.card, padding:14, marginBottom:14, fontSize:13, color:G.textMuted, lineHeight:1.6 }}>
      📦 Snapshots are reusable bundles of <strong style={{ color:G.text }}>workflows + document templates + forms + AI agents + price book</strong>.
      Build one perfect HVAC starter pack and push it into every new client account in seconds.
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14, gap:10, flexWrap:'wrap' }}>
      <Btn v="cyan" onClick={async ()=>{
        if (!confirm('Install the 9 pre-built industry kits (HVAC, Plumbing, Electrical, Roofing, Garage Door, Cleaning, Landscaping, GC)?')) return;
        try { const r = await api('/api/agency/snapshots/seed-library', { method:'POST', body:{ refresh: false }});
          load(); toast(`Installed ${r.inserted} new + refreshed ${r.updated}`);
        } catch(e) { toast(e.message,'error'); }
      }}>📚 Install Pre-Built Library</Btn>
      <Btn onClick={()=>document.getElementById('snap-import').click()}>📥 Import JSON</Btn>
      <input id="snap-import" type="file" accept="application/json" style={{ display:'none' }} onChange={async e=>{
        const file = e.target.files?.[0]; if (!file) return;
        const txt = await file.text();
        try {
          const snapshot = JSON.parse(txt);
          await api('/api/agency/snapshots/import', { method:'POST', body:{ snapshot }});
          load(); toast('Imported');
        } catch(err) { toast('Import failed: ' + err.message, 'error'); }
        e.target.value = '';
      }}/>
      <Btn onClick={()=>setCapture(true)}>📸 Capture from account</Btn>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))', gap:14 }}>
      {snapshots.map(s => {
        const counts = {
          workflows: (s.payload?.workflows||[]).length,
          docs: (s.payload?.document_templates||[]).length,
          forms: (s.payload?.forms||[]).length,
          agents: (s.payload?.agents||[]).length,
          priceBook: (s.payload?.price_book||[]).length,
        };
        return <div key={s.id} style={styles.card}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <strong style={{ fontSize:15 }}>{s.name}</strong>
              {s.industry && <Badge c={G.cyan} bg={G.cyanLight} style={{ marginLeft:6 }}>{s.industry}</Badge>}
            </div>
            <div style={{ display:'flex', gap:4 }}>
              <Btn sz="xs" onClick={()=>window.open(`/api/agency/snapshots/${s.id}/export`, '_blank')}>📤 Export</Btn>
              <Btn sz="xs" v="danger" onClick={()=>remove(s)}>×</Btn>
            </div>
          </div>
          {s.description && <div style={{ fontSize:13, color:G.textMuted, marginTop:6, lineHeight:1.5 }}>{s.description}</div>}
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:10 }}>
            {counts.workflows > 0 && <Badge c={G.purple} bg={G.purpleDim}>{counts.workflows} workflows</Badge>}
            {counts.docs > 0 && <Badge c={G.cyan} bg={G.cyanLight}>{counts.docs} doc templates</Badge>}
            {counts.forms > 0 && <Badge c={G.green} bg={G.greenLight}>{counts.forms} forms</Badge>}
            {counts.agents > 0 && <Badge c={G.orange} bg={G.orangeLight}>{counts.agents} AI agents</Badge>}
            {counts.priceBook > 0 && <Badge c={G.pink} bg={G.pink+'22'}>{counts.priceBook} price items</Badge>}
          </div>
        </div>;
      })}
      {snapshots.length === 0 && <div style={{ ...styles.card, padding:32, gridColumn:'1/-1', textAlign:'center', color:G.textMuted }}>No snapshots yet. Capture one from an existing account.</div>}
    </div>

    <CaptureSnapshotModal open={capture} accounts={accounts} onClose={()=>setCapture(false)} onCreated={()=>{ load(); setCapture(false); toast('Snapshot captured'); }}/>
  </>;
}

function CaptureSnapshotModal({ open, accounts, onClose, onCreated }) {
  const [form, setForm] = useState({ accountId:'', name:'', description:'', kinds:['workflows','document_templates','forms','agents','price_book'] });
  if (!open) return null;
  const toggleKind = k => setForm({ ...form, kinds: form.kinds.includes(k) ? form.kinds.filter(x=>x!==k) : [...form.kinds, k] });
  const submit = async () => {
    if (!form.accountId || !form.name) return alert('Pick an account and give it a name');
    try { await api(`/api/agency/snapshots/from-account/${form.accountId}`, { method:'POST', body: form }); onCreated(); }
    catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 14px 0' }}>📸 Capture Snapshot</h2>
    <Field label="Source Account">
      <Select value={form.accountId} onChange={e=>setForm({...form,accountId:e.target.value})}>
        <option value="">— select —</option>
        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </Select>
    </Field>
    <Field label="Snapshot Name *"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="HVAC Starter Kit v2"/></Field>
    <Field label="Description"><Textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></Field>
    <Field label="What to capture">
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
        {[['workflows','⚡ Workflows'],['document_templates','📄 Document Templates'],['forms','📝 Forms'],['agents','🤖 AI Agents'],['price_book','🏷️ Price Book']].map(([k,l]) => (
          <label key={k} style={{ display:'flex', alignItems:'center', gap:8, padding:8, background: form.kinds.includes(k)?G.purpleDim:G.input, border:`1px solid ${form.kinds.includes(k)?G.purple:G.cardBorder}`, borderRadius:8, cursor:'pointer' }}>
            <input type="checkbox" checked={form.kinds.includes(k)} onChange={()=>toggleKind(k)}/>
            <span style={{ fontSize:13 }}>{l}</span>
          </label>
        ))}
      </div>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={submit}>Capture</Btn>
    </div>
  </Modal>;
}

function PushTab({ toast }) {
  const [snapshots, setSnapshots] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [snapshotId, setSnapshotId] = useState('');
  const [accountIds, setAccountIds] = useState([]);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    Promise.all([api('/api/agency/snapshots'), api('/api/agency/accounts')])
      .then(([s,a]) => { setSnapshots(s); setAccounts(a); })
      .catch(()=>{});
  }, []);

  const toggleAccount = id => setAccountIds(accountIds.includes(id) ? accountIds.filter(x=>x!==id) : [...accountIds, id]);
  const selectAll = () => setAccountIds(accounts.map(a=>a.id));
  const clearAll = () => setAccountIds([]);

  const push = async () => {
    if (!snapshotId || accountIds.length === 0) return;
    if (replace && !confirm('Replace existing workflows on these accounts? Protected workflows are kept.')) return;
    setBusy(true);
    try {
      const r = await api(`/api/agency/snapshots/${snapshotId}/push`, { method:'POST', body:{ accountIds, replaceExisting: replace } });
      setResult(r);
      toast(`Pushed to ${r.results.length} accounts`);
    } catch(e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  const snap = snapshots.find(s => s.id === snapshotId);

  return <div>
    <div style={{ ...styles.card, padding:14, marginBottom:14, fontSize:13, color:G.textMuted, lineHeight:1.6 }}>
      🚀 Pick a snapshot, choose target accounts, push. The new workflows + templates + forms + agents + price-book items will be installed instantly.
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>1. Pick Snapshot</h3>
        {snapshots.map(s => (
          <div key={s.id} onClick={()=>setSnapshotId(s.id)} style={{ ...styles.card, padding:12, marginBottom:8, cursor:'pointer', borderColor: snapshotId===s.id?G.purple:G.cardBorder, background: snapshotId===s.id?G.purpleDim:G.card }}>
            <strong style={{ fontSize:14 }}>{s.name}</strong>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{s.description}</div>
          </div>
        ))}
        {snapshots.length === 0 && <div style={{ color:G.textMuted, fontSize:13, padding:20, textAlign:'center' }}>No snapshots yet — create one in the Library tab.</div>}
      </div>

      <div style={styles.card}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
          <h3 style={{ margin:0, fontSize:16 }}>2. Target Accounts ({accountIds.length})</h3>
          <div style={{ display:'flex', gap:6 }}>
            <Btn sz="xs" onClick={selectAll}>All</Btn>
            <Btn sz="xs" onClick={clearAll}>None</Btn>
          </div>
        </div>
        {accounts.map(a => (
          <label key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, cursor:'pointer', background: accountIds.includes(a.id) ? G.purpleDim : 'transparent', marginBottom:2 }}>
            <input type="checkbox" checked={accountIds.includes(a.id)} onChange={()=>toggleAccount(a.id)}/>
            <Av name={a.name} sz={28}/>
            <div style={{ flex:1 }}>
              <strong style={{ fontSize:13 }}>{a.name}</strong>
              <div style={{ fontSize:11, color:G.textMuted }}>{a.industry} · {a.plan}</div>
            </div>
          </label>
        ))}
      </div>
    </div>

    <div style={{ ...styles.card, padding:18, marginTop:14 }}>
      <label style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
        <input type="checkbox" checked={replace} onChange={e=>setReplace(e.target.checked)}/>
        <span>Replace existing non-protected workflows (otherwise they're added alongside)</span>
      </label>
      <Btn v="primary" onClick={push} disabled={!snapshotId || accountIds.length===0 || busy}>
        {busy ? <Spinner/> : `🚀 Push "${snap?.name||'snapshot'}" to ${accountIds.length} account${accountIds.length===1?'':'s'}`}
      </Btn>
    </div>

    {result && <div style={{ ...styles.card, marginTop:14, borderColor: G.green+'66' }}>
      <h3 style={{ margin:'0 0 12px 0', color:G.green }}>✅ Push Complete</h3>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:10 }}>Snapshot "{result.snapshot}" deployed to {result.results.length} accounts:</div>
      {result.results.map(r => {
        const a = accounts.find(x => x.id === r.accountId);
        return <div key={r.accountId} style={{ padding:'8px 10px', borderTop:`1px solid ${G.border}`, fontSize:13 }}>
          <strong>{a?.name||r.accountId}</strong>: {Object.entries(r).filter(([k,v]) => k !== 'accountId' && v > 0).map(([k,v]) => `${v} ${k}`).join(' · ') || 'nothing pushed'}
        </div>;
      })}
    </div>}
  </div>;
}

function AbTab({ toast }) {
  const [tests, setTests] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name:'', scope:'ai_playbook', variantA:'', variantB:'', metric:'booking_rate' });

  const load = () => api('/api/growth/ab-tests').then(setTests).catch(()=>{});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return alert('Name required');
    try { await api('/api/growth/ab-tests', { method:'POST', body: form }); setCreating(false); load(); toast('Test started'); }
    catch(e) { toast(e.message,'error'); }
  };

  return <>
    <div style={{ ...styles.card, padding:14, marginBottom:14, fontSize:13, color:G.textMuted, lineHeight:1.5, borderColor:G.purple+'66' }}>
      🧪 A/B testing is agency-level only. Create variants here, test across all sub-accounts simultaneously, see what wins, then push the winning variant to every account in one click. Sub-accounts never see the variants — they just get the best playbook.
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
      <Btn v="primary" onClick={()=>setCreating(true)}>+ New A/B Test</Btn>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(380px,1fr))', gap:14 }}>
      {tests.map(t => {
        const aRate = t.a_count > 0 ? Math.round((t.a_wins/t.a_count)*100) : 0;
        const bRate = t.b_count > 0 ? Math.round((t.b_wins/t.b_count)*100) : 0;
        const winner = t.winner;
        return <div key={t.id} style={{ ...styles.card, borderColor: winner ? G.green+'66' : G.cardBorder }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <strong>{t.name}</strong>
            <Badge c={winner?G.green:G.cyan} bg={(winner?G.green:G.cyan)+'22'}>{winner?`Winner: ${winner}`:t.status}</Badge>
          </div>
          <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>{t.metric} · {t.scope}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div style={{ ...styles.card, padding:10, background:winner==='A'?G.greenLight:G.input, borderColor: winner==='A'?G.green+'66':G.cardBorder }}>
              <div style={{ fontSize:11, color:G.textMuted, fontWeight:700 }}>VARIANT A</div>
              <div style={{ fontSize:24, fontWeight:800, color: winner==='A'?G.green:G.text }}>{aRate}%</div>
              <div style={{ fontSize:11, color:G.textMuted }}>{t.a_wins}/{t.a_count} wins</div>
            </div>
            <div style={{ ...styles.card, padding:10, background:winner==='B'?G.greenLight:G.input, borderColor: winner==='B'?G.green+'66':G.cardBorder }}>
              <div style={{ fontSize:11, color:G.textMuted, fontWeight:700 }}>VARIANT B</div>
              <div style={{ fontSize:24, fontWeight:800, color: winner==='B'?G.green:G.text }}>{bRate}%</div>
              <div style={{ fontSize:11, color:G.textMuted }}>{t.b_wins}/{t.b_count} wins</div>
            </div>
          </div>
          {winner && <Btn v="primary" sz="sm" full style={{ marginTop:10 }} onClick={async ()=>{
            if (!confirm(`Push variant ${winner} to all sub-accounts as their default playbook?`)) return;
            try { const r = await api(`/api/growth/ab-tests/${t.id}/push-winner`, { method:'POST' }); toast(`Pushed to ${r.pushedTo} accounts`); }
            catch(e) { toast(e.message,'error'); }
          }}>🚀 Push winner to all accounts</Btn>}
        </div>;
      })}
      {tests.length===0 && <div style={{ ...styles.card, padding:32, textAlign:'center', color:G.textMuted, gridColumn:'1/-1' }}>No A/B tests yet. Create one to start optimizing.</div>}
    </div>

    <Modal open={creating} onClose={()=>setCreating(false)} width={620}>
      <h2 style={{ margin:'0 0 14px 0' }}>New A/B Test</h2>
      <Field label="Test Name *"><Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="Booking close rate v3"/></Field>
      <Field label="Scope">
        <Select value={form.scope} onChange={e=>setForm({...form, scope:e.target.value})}>
          <option value="ai_playbook">AI Playbook (SMS chat)</option>
          <option value="estimate_followup">Estimate Follow-up Copy</option>
          <option value="invoice_reminder">Invoice Reminder Copy</option>
        </Select>
      </Field>
      <Field label="Metric">
        <Select value={form.metric} onChange={e=>setForm({...form, metric:e.target.value})}>
          <option value="booking_rate">Booking conversion</option>
          <option value="reply_rate">Reply rate</option>
          <option value="payment_rate">Payment rate</option>
        </Select>
      </Field>
      <Field label="Variant A"><Textarea value={form.variantA} onChange={e=>setForm({...form, variantA:e.target.value})} style={{ minHeight:100 }}/></Field>
      <Field label="Variant B"><Textarea value={form.variantB} onChange={e=>setForm({...form, variantB:e.target.value})} style={{ minHeight:100 }}/></Field>
      <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
        <Btn onClick={()=>setCreating(false)}>Cancel</Btn>
        <Btn v="primary" onClick={create}>Start Test</Btn>
      </div>
    </Modal>
  </>;
}

function InstallmentPlanCard({ accountId, toast = () => {} }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ totalDollars:'', weeklyDollars:'', postMonthlyDollars:'' });
  const [payment, setPayment] = useState({ amountDollars:'', method:'', note:'' });

  const load = () => api(`/api/admin-panel/installments/${accountId}`).then(setData).catch(()=>{});
  useEffect(() => { load(); }, [accountId]);
  useEffect(() => {
    if (data?.plan) {
      setForm({
        totalDollars: data.plan.totalCents ? data.plan.totalCents / 100 : '',
        weeklyDollars: data.plan.weeklyCents ? data.plan.weeklyCents / 100 : '',
        postMonthlyDollars: data.plan.postMonthlyCents ? data.plan.postMonthlyCents / 100 : '',
      });
    }
  }, [data]);

  const savePlan = async () => {
    try {
      await api(`/api/admin-panel/installments/${accountId}`, { method:'PATCH', body:{
        totalCents: form.totalDollars === '' ? null : Math.round(Number(form.totalDollars) * 100),
        weeklyCents: form.weeklyDollars === '' ? null : Math.round(Number(form.weeklyDollars) * 100),
        postMonthlyCents: form.postMonthlyDollars === '' ? null : Math.round(Number(form.postMonthlyDollars) * 100),
      }});
      load(); setEditing(false); toast('✓ Installment plan saved');
    } catch (e) { toast(e.message, 'error'); }
  };

  const recordPayment = async () => {
    if (!payment.amountDollars) return;
    try {
      const r = await api(`/api/admin-panel/installments/${accountId}/record-payment`, { method:'POST', body:{
        amountCents: Math.round(Number(payment.amountDollars) * 100),
        method: payment.method || null,
        note: payment.note || null,
      }});
      setPayment({ amountDollars:'', method:'', note:'' });
      load();
      if (r.recurringSubId) toast(`✓ Plan complete! Auto-created $${(data.plan.postMonthlyCents/100).toFixed(0)}/mo subscription`);
      else if (r.warning) toast(r.warning, 'warn');
      else toast('✓ Payment recorded');
    } catch (e) { toast(e.message, 'error'); }
  };

  if (!data) return null;
  const p = data.plan;
  const hasPlan = p.totalCents > 0;

  return <div style={{ padding:14, background:G.cyanLight, border:`1px solid ${G.cyan}44`, borderRadius:10 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
      <div style={{ fontSize:13, fontWeight:700 }}>💰 Installment Setup Fee {p.isComplete && <Badge c={G.green} bg={G.greenLight}>✓ COMPLETE</Badge>}</div>
      {hasPlan && <Btn sz="sm" v="ghost" onClick={()=>setEditing(!editing)}>{editing ? 'Cancel' : '✏️ Edit plan'}</Btn>}
    </div>
    <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>
      For deals like Electric Medic ($500/wk × 12 → $6,000 setup → auto-flip to $497/mo). Log each payment as it comes in; the recurring sub auto-creates on the final payment.
    </div>

    {(!hasPlan || editing) && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:10 }}>
      <Field label="Total setup fee ($)">
        <Input type="number" value={form.totalDollars} onChange={e=>setForm({...form, totalDollars:e.target.value})} placeholder="6000"/>
      </Field>
      <Field label="Weekly payment ($)">
        <Input type="number" value={form.weeklyDollars} onChange={e=>setForm({...form, weeklyDollars:e.target.value})} placeholder="500"/>
      </Field>
      <Field label="Then monthly recurring ($)">
        <Input type="number" value={form.postMonthlyDollars} onChange={e=>setForm({...form, postMonthlyDollars:e.target.value})} placeholder="497"/>
      </Field>
      <div style={{ gridColumn:'1/-1', display:'flex', justifyContent:'flex-end' }}>
        <Btn v="primary" sz="sm" onClick={savePlan}>{hasPlan ? 'Save Changes' : 'Create Plan'}</Btn>
      </div>
    </div>}

    {hasPlan && !editing && <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:10 }}>
        <Mini label="Total" value={fmtUsd(p.totalCents/100)} small/>
        <Mini label="Paid" value={fmtUsd(p.paidCents/100)} small/>
        <Mini label="Remaining" value={fmtUsd(p.remainingCents/100)} small/>
        <Mini label="Then" value={`${fmtUsd((p.postMonthlyCents||0)/100)}/mo`} small/>
      </div>
      <div style={{ marginBottom:10 }}>
        <div style={{ height:8, background:G.input, borderRadius:4, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${p.progressPct}%`, background: p.isComplete?G.green:G.cyan, transition:'width .3s' }}/>
        </div>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>{p.progressPct}% paid · {data.payments.length} payment{data.payments.length===1?'':'s'} recorded</div>
      </div>

      {!p.isComplete && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 100px', gap:8, marginBottom:10, alignItems:'end' }}>
        <Field label="Record payment ($)">
          <Input type="number" value={payment.amountDollars} onChange={e=>setPayment({...payment, amountDollars:e.target.value})} placeholder={p.weeklyCents ? (p.weeklyCents/100).toString() : '500'}/>
        </Field>
        <Field label="Method">
          <Input value={payment.method} onChange={e=>setPayment({...payment, method:e.target.value})} placeholder="Stripe / Check / ACH"/>
        </Field>
        <Field label="Note">
          <Input value={payment.note} onChange={e=>setPayment({...payment, note:e.target.value})} placeholder="Week 1, etc."/>
        </Field>
        <Btn v="primary" onClick={recordPayment}>+ Record</Btn>
      </div>}

      {data.payments.length > 0 && <details style={{ marginTop:8 }}>
        <summary style={{ cursor:'pointer', fontSize:12, color:G.textMuted }}>Payment history ({data.payments.length})</summary>
        <div style={{ marginTop:6, maxHeight:180, overflow:'auto' }}>
          {data.payments.map(pmt => (
            <div key={pmt.id} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:12, borderBottom:`1px solid ${G.cardBorder}` }}>
              <span>{new Date(pmt.paid_at).toLocaleDateString()}{pmt.method?` · ${pmt.method}`:''}{pmt.note?` · ${pmt.note}`:''}</span>
              <strong>{fmtUsd(pmt.amount_cents/100)}</strong>
            </div>
          ))}
        </div>
      </details>}
    </>}
  </div>;
}

// Agency-wide platform-fee editor. Sets the % skimmed off every sub-account
// invoice via Stripe Connect application_fee, tiered by plan. Per-account
// overrides live inside the sub-account CRM modal (💰 Pricing tab).
function PlatformFeesCard({ toast = () => {} }) {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = () => api('/api/agency/platform-fees').then(setData).catch(()=>{});
  React.useEffect(() => { load(); }, []);
  if (!data) return null;
  const plans = Object.keys(data.effective || {});
  const save = async (plan, pct) => {
    setBusy(true);
    try {
      const next = { ...(data.overrides || {}), [plan]: Number(pct) };
      await api('/api/agency/platform-fees', { method:'PATCH', body: next });
      toast(`✓ ${plan} fee → ${pct}%`, 'success');
      load();
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };
  const reset = async (plan) => {
    setBusy(true);
    try {
      const next = { ...(data.overrides || {}) };
      delete next[plan];
      await api('/api/agency/platform-fees', { method:'PATCH', body: next });
      toast(`${plan} reset to default`, 'success');
      load();
    } catch (e) { toast(e.message, 'error'); }
    setBusy(false);
  };
  return <div style={{ ...styles.card, padding:16, marginBottom:14 }}>
    <h3 style={{ margin:'0 0 6px', fontSize:16 }}>💰 Stripe Platform Fees</h3>
    <div style={{ fontSize:12, color:G.textMuted, marginBottom:12 }}>
      The % we skim off every sub-account invoice via Stripe Connect. Set per plan below — or set a per-account override / off toggle in each sub-account's <strong>Pricing</strong> tab.
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:10 }}>
      {plans.map(plan => {
        const isOverride = data.overrides && data.overrides[plan] != null;
        return <div key={plan} style={{ ...styles.card, padding:12, borderColor: isOverride ? G.purple+'66' : G.cardBorder }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
            <strong style={{ fontSize:14 }}>{plan}</strong>
            <span style={{ fontSize:10, color:G.textMuted }}>default {data.defaults[plan] ?? '—'}%</span>
          </div>
          <PlanFeeInput value={data.effective[plan]} onSave={pct => save(plan, pct)} disabled={busy}/>
          {isOverride && <button onClick={()=>reset(plan)} disabled={busy} style={{ marginTop:6, fontSize:11, color:G.textMuted, background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>reset to default</button>}
        </div>;
      })}
    </div>
  </div>;
}
function PlanFeeInput({ value, onSave, disabled }) {
  const [v, setV] = React.useState(String(value));
  React.useEffect(() => { setV(String(value)); }, [value]);
  const commit = () => { const n = Number(v); if (!Number.isFinite(n) || n < 0 || n > 15) return; if (n !== value) onSave(n); };
  return <div style={{ display:'flex', gap:4, alignItems:'center' }}>
    <input type="number" step="0.05" min="0" max="15" value={v} onChange={e=>setV(e.target.value)} onBlur={commit} onKeyDown={e => e.key === 'Enter' && commit()} disabled={disabled}
      style={{ width:'80px', padding:'6px 8px', border:`1px solid ${G.border}`, background:G.input, color:G.text, borderRadius:6, fontSize:14, fontWeight:700 }}/>
    <span style={{ color:G.textMuted }}>%</span>
  </div>;
}

// Agency Team — invite real users (super_admin, agency_admin, csm, ea) to the
// agency org. These users get real login credentials + role-scoped access.
// Distinct from EA read-only tokens (below), which are just bookmarkable
// magic-link URLs, no login.
function AgencyTeamCard({ toast = () => {}, me }) {
  const [team, setTeam] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [justCreated, setJustCreated] = React.useState(null);
  const [form, setForm] = React.useState({ email:'', firstName:'', lastName:'', phone:'', role:'agency_admin' });
  const load = () => api('/api/agency/team').then(r => setTeam(r.team || [])).catch(()=>setTeam([]));
  React.useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email.trim() || !form.firstName.trim()) return toast('Email + first name required', 'error');
    setCreating(true);
    try {
      const r = await api('/api/agency/team', { method:'POST', body: form });
      setJustCreated(r);
      setForm({ email:'', firstName:'', lastName:'', phone:'', role:'agency_admin' });
      load();
      toast('✓ Team member added');
    } catch (e) { toast(e.message, 'error'); }
    finally { setCreating(false); }
  };
  const changeRole = async (u, role) => {
    try { await api(`/api/agency/team/${u.id}`, { method:'PATCH', body:{ role }}); load(); toast(`✓ ${u.displayName} → ${role}`); }
    catch (e) { toast(e.message, 'error'); }
  };
  const deactivate = async (u) => {
    if (!confirm(`Deactivate ${u.displayName}? They lose access immediately.`)) return;
    try { await api(`/api/agency/team/${u.id}`, { method:'DELETE' }); load(); toast('Deactivated'); }
    catch (e) { toast(e.message, 'error'); }
  };

  const ROLE_LABELS = {
    super_admin:  { label:'Super Admin',  color: G.red,    desc:'Full access — billing, add team, all sub-accounts' },
    agency_admin: { label:'Agency Admin', color: G.purple, desc:'Manage sub-accounts + workflows; no billing / no team edits' },
    csm:          { label:'CSM',          color: G.cyan,   desc:'Customer success — view + message assigned sub-accounts' },
    ea:           { label:'EA',           color: G.green,  desc:'Executive assistant — read-only dashboards' },
  };

  return <div style={{ ...styles.card, padding:16, marginBottom:14 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
      <div>
        <h3 style={{ margin:0, fontSize:16 }}>👥 Agency Team</h3>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>Real login users at your agency level. Not the same as EA read-only links below.</div>
      </div>
    </div>

    {team === null ? <Spinner sz={18}/> : team.length === 0 ? (
      <div style={{ padding:14, color:G.textMuted, fontSize:13, textAlign:'center' }}>Just you. Add teammates below.</div>
    ) : (
      <div style={{ display:'grid', gap:8, marginTop:10 }}>
        {team.map(u => {
          const R = ROLE_LABELS[u.role] || { label:u.role, color:G.textMuted, desc:'' };
          const isMe = u.id === me?.id;
          return <div key={u.id} style={{ display:'flex', gap:12, alignItems:'center', padding:'10px 12px', border:`1px solid ${G.cardBorder}`, borderRadius:8, background: u.status==='disabled'? G.input : 'transparent' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                <strong style={{ fontSize:14 }}>{u.displayName}</strong>
                {isMe && <Badge c={G.text} bg={G.input}>you</Badge>}
                <Badge c={R.color} bg={R.color+'22'}>{R.label}</Badge>
                {u.status === 'disabled' && <Badge c={G.textMuted} bg={G.input}>disabled</Badge>}
              </div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
            </div>
            {u.status !== 'disabled' && !isMe && <>
              <Select value={u.role} onChange={e=>changeRole(u, e.target.value)} style={{ fontSize:12, padding:'6px 8px' }}>
                {Object.keys(ROLE_LABELS).map(k => <option key={k} value={k}>{ROLE_LABELS[k].label}</option>)}
              </Select>
              <Btn sz="xs" v="danger" onClick={()=>deactivate(u)}>Deactivate</Btn>
            </>}
          </div>;
        })}
      </div>
    )}

    <div style={{ marginTop:14, padding:12, background:G.input, borderRadius:8 }}>
      <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>Add a team member</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
        <Input placeholder="First name *" value={form.firstName} onChange={e=>setForm({...form, firstName:e.target.value})}/>
        <Input placeholder="Last name" value={form.lastName} onChange={e=>setForm({...form, lastName:e.target.value})}/>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:8, marginBottom:8 }}>
        <Input placeholder="Email *" type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
        <Input placeholder="Phone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/>
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
        <div style={{ flex:1 }}>
          <Select value={form.role} onChange={e=>setForm({...form, role:e.target.value})} style={{ width:'100%' }}>
            {Object.keys(ROLE_LABELS).map(k => <option key={k} value={k}>{ROLE_LABELS[k].label} — {ROLE_LABELS[k].desc}</option>)}
          </Select>
        </div>
        <Btn v="primary" onClick={invite} disabled={creating}>{creating ? '…' : '+ Add'}</Btn>
      </div>
    </div>

    {justCreated?.tempPassword && (
      <div style={{ marginTop:12, padding:12, background:G.green+'11', border:`1px solid ${G.green}66`, borderRadius:8 }}>
        <div style={{ fontWeight:700, fontSize:13, color:G.green, marginBottom:4 }}>✓ Team member added</div>
        <div style={{ fontSize:12, color:G.text, lineHeight:1.6 }}>
          Share these credentials with them (they can reset the password from Settings → Security):<br/>
          <strong>Password:</strong> <code style={{ background:G.card, padding:'2px 6px', borderRadius:4 }}>{justCreated.tempPassword}</code>
          <Btn sz="xs" onClick={()=>{ navigator.clipboard?.writeText(justCreated.tempPassword); toast('Copied'); }} style={{ marginLeft:8 }}>📋</Btn>
        </div>
      </div>
    )}
  </div>;
}

// EA (executive assistant) read-only access links. Each token is a bookmarkable
// URL scoped to a read-only sub-account health dashboard — no admin data, no
// session. Revocable + tracked by last-used timestamp.
function EATokensCard({ toast = () => {} }) {
  const [tokens, setTokens] = React.useState(null);
  const [label, setLabel] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [justCreated, setJustCreated] = React.useState(null);
  const load = () => api('/api/admin-panel/ea-tokens').then(d => setTokens(d.tokens || [])).catch(()=>{});
  React.useEffect(() => { load(); }, []);
  const create = async () => {
    if (!label.trim()) return toast('Give the link a label (e.g. "Sarah — EA")', 'error');
    setCreating(true);
    try {
      const r = await api('/api/admin-panel/ea-tokens', { method:'POST', body:{ label: label.trim() }});
      setJustCreated(r);
      setLabel('');
      load();
      toast('✓ Access link generated');
    } catch (e) { toast(e.message, 'error'); }
    finally { setCreating(false); }
  };
  const revoke = async (id, lbl) => {
    if (!confirm(`Revoke ${lbl}'s access link? They lose access immediately.`)) return;
    try { await api(`/api/admin-panel/ea-tokens/${id}/revoke`, { method:'POST' }); load(); toast('Revoked'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const del = async (id, lbl) => {
    if (!confirm(`Permanently delete ${lbl}'s revoked link record?`)) return;
    try { await api(`/api/admin-panel/ea-tokens/${id}`, { method:'DELETE' }); load(); }
    catch (e) { toast(e.message, 'error'); }
  };
  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => toast('✓ Link copied — paste to your EA'));
  };
  return <div style={{ ...styles.card, padding:16, marginBottom:14 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 }}>
      <div>
        <div style={{ fontWeight:700, fontSize:15 }}>🔑 Executive Assistant Access</div>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>
          Read-only bookmarkable links to a sub-account health dashboard. Shows payment status + churn flags only — no CRM, no data drill-in, no session.
        </div>
      </div>
    </div>
    <div style={{ display:'flex', gap:8, alignItems:'flex-end', marginBottom:14 }}>
      <Field label="Label (who is it for?)" style={{ flex:1 }}>
        <Input value={label} onChange={e=>setLabel(e.target.value)} placeholder='e.g. "Sarah — EA"'/>
      </Field>
      <Btn v="primary" onClick={create} disabled={creating || !label.trim()}>{creating?'…':'+ Generate Link'}</Btn>
    </div>

    {justCreated && (
      <div style={{ ...styles.card, background:G.green+'11', border:`1px solid ${G.green}44`, padding:14, marginBottom:14 }}>
        <div style={{ fontSize:13, fontWeight:700, color:G.green, marginBottom:6 }}>✅ Link ready for {justCreated.label}</div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <input readOnly value={justCreated.url}
            onFocus={e=>e.target.select()}
            style={{ ...styles.inp, flex:1, fontFamily:'ui-monospace,monospace', fontSize:12 }}/>
          <Btn sz="sm" v="primary" onClick={()=>copyUrl(justCreated.url)}>📋 Copy</Btn>
        </div>
        <div style={{ fontSize:11, color:G.textMuted, marginTop:8 }}>Send this URL over the safest channel you have. Whoever has it can view sub-account health without logging in.</div>
      </div>
    )}

    {tokens === null ? <Spinner sz={20}/> : tokens.length === 0 ? (
      <div style={{ color:G.textMuted, fontSize:13, padding:'8px 0' }}>No access links yet.</div>
    ) : (
      <table style={{ width:'100%', fontSize:13 }}>
        <thead><tr style={{ color:G.textMuted, fontSize:11, textTransform:'uppercase' }}>
          <th style={{ textAlign:'left', padding:'6px 8px' }}>Label</th>
          <th style={{ textAlign:'left', padding:'6px 8px' }}>Status</th>
          <th style={{ textAlign:'right', padding:'6px 8px' }}>Uses</th>
          <th style={{ textAlign:'left', padding:'6px 8px' }}>Last Used</th>
          <th style={{ textAlign:'left', padding:'6px 8px' }}>Created</th>
          <th style={{ textAlign:'right', padding:'6px 8px' }}></th>
        </tr></thead>
        <tbody>
          {tokens.map(t => (
            <tr key={t.id} style={{ borderTop:`1px solid ${G.border}` }}>
              <td style={{ padding:'6px 8px', fontWeight:600 }}>{t.label}</td>
              <td style={{ padding:'6px 8px' }}>
                <Badge c={t.active?G.green:G.textMuted} bg={(t.active?G.green:G.textMuted)+'22'}>{t.active?'Active':'Revoked'}</Badge>
              </td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>{t.use_count || 0}</td>
              <td style={{ padding:'6px 8px' }}>{t.last_used_at ? new Date(t.last_used_at).toLocaleString() : '—'}</td>
              <td style={{ padding:'6px 8px' }}>{new Date(t.created_at).toLocaleDateString()}</td>
              <td style={{ padding:'6px 8px', textAlign:'right' }}>
                {t.active ? <Btn sz="xs" v="danger" onClick={()=>revoke(t.id, t.label)}>Revoke</Btn>
                          : <Btn sz="xs" onClick={()=>del(t.id, t.label)}>Delete</Btn>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>;
}

function KillSwitchesCard({ toast = () => {} }) {
  const [data, setData] = useState(null);
  const load = () => api('/api/admin-panel/kill-switches').then(setData).catch(()=>{});
  useEffect(() => { load(); }, []);
  const flip = async (key, on) => {
    try {
      const r = await api('/api/admin-panel/kill-switches', { method:'PATCH', body:{ key, on } });
      setData(r);
      toast(`${key} ${on?'DISABLED':'enabled'}`, on ? 'warn' : 'success');
    } catch (e) { toast(e.message, 'error'); }
  };
  if (!data) return null;
  const labels = {
    AI_SMS_DISABLED: { name:'AI SMS replies', desc:'Auto-responses to inbound SMS across all sub-accounts' },
    AI_VOICE_DISABLED: { name:'AI Voice calls', desc:'Speed-to-lead outbound + AI agent calls' },
    WORKFLOWS_DISABLED: { name:'Workflows', desc:'All workflow enrollments + scheduled steps (state preserved — flip back on to resume)' },
  };
  const anyOn = data.switches.some(s => s.on);
  return <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor: (anyOn?G.red:G.yellow)+'66' }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
      <span style={{ fontSize:18 }}>{anyOn ? '🛑' : '⚙️'}</span>
      <strong style={{ fontSize:14 }}>Kill Switches</strong>
      <span style={{ fontSize:12, color:G.textMuted }}>Platform-wide on/off for ALL sub-accounts. Flip ON = features pause until you flip back OFF.</span>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:10 }}>
      {data.switches.map(s => {
        const meta = labels[s.key] || { name: s.key, desc: '' };
        return <div key={s.key} style={{ padding:'10px 12px', background: s.on ? G.redLight : G.greenLight, border:`1px solid ${(s.on?G.red:G.green)+'44'}`, borderRadius:6 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700 }}>{meta.name}</div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{meta.desc}</div>
              <div style={{ fontSize:10, color:G.textMuted, marginTop:4 }}>
                Source: {s.source}{s.overriddenByEnv ? ' (Fly secret — UI override disabled)' : ''}
              </div>
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:6, cursor: s.overriddenByEnv ? 'not-allowed' : 'pointer', opacity: s.overriddenByEnv ? 0.5 : 1 }}>
              <input type="checkbox" checked={!s.on} disabled={s.overriddenByEnv} onChange={(e) => flip(s.key, !e.target.checked)}/>
              <span style={{ fontSize:12, fontWeight:600, color: s.on ? G.red : G.green }}>{s.on ? 'PAUSED' : 'ON'}</span>
            </label>
          </div>
        </div>;
      })}
    </div>
  </div>;
}

function OperationsHealthCard({ toast = () => {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const load = () => api('/api/admin-panel/ops-health').then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, []);
  const fireTest = async () => {
    try {
      const r = await api('/api/admin-panel/ops-health/test-alert', { method:'POST' });
      const msg = `Test alert fired.\n\nSMS: ${r.sent?.sms || r.sent?.smsError || 'not configured'}\nEmail: ${r.sent?.email || r.sent?.emailError || 'not configured'}`;
      alert(msg);
    } catch (e) { toast(e.message, 'error'); }
  };
  if (loading) return null;
  const allOk = data?.ok;
  const failed = (data?.checks || []).filter(c => !c.ok);
  const borderColor = allOk ? G.green : G.red;
  return <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor: borderColor+'66' }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }} onClick={()=>setCollapsed(!collapsed)}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:18 }}>{allOk ? '🟢' : '🔴'}</span>
        <strong style={{ fontSize:14 }}>Operations Health</strong>
        <span style={{ fontSize:12, color:G.textMuted }}>
          {allOk ? `All ${data.checks.length} probes passing` : `${failed.length}/${data.checks.length} failing`}
        </span>
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <span style={{ fontSize:11, color:G.textMuted }}>auto-refresh 60s</span>
        <Btn sz="sm" onClick={(e)=>{ e.stopPropagation(); fireTest(); }}>🧪 Test Alert</Btn>
        <span style={{ fontSize:14, color:G.textMuted }}>{collapsed ? '▶' : '▼'}</span>
      </div>
    </div>
    {!collapsed && <div style={{ marginTop:10, display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:8 }}>
      {(data?.checks || []).map(c => (
        <div key={c.name} style={{ padding:'8px 10px', background: c.ok ? G.greenLight : G.redLight, border:`1px solid ${(c.ok?G.green:G.red)+'44'}`, borderRadius:6 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
            <span>{c.ok ? '✅' : '❌'}</span>
            <strong style={{ textTransform:'uppercase' }}>{c.name}</strong>
          </div>
          <div style={{ fontSize:11, color:G.textMuted, marginTop:4, fontFamily:'monospace', wordBreak:'break-word' }}>
            {c.error || (typeof c.detail === 'string' ? c.detail : JSON.stringify(c.detail || {}))}
          </div>
        </div>
      ))}
    </div>}
  </div>;
}

function Stat({ label, value, sub, color }) {
  return <div style={{ ...styles.card, borderColor:color+'66' }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, marginTop:6 }}>{value}</div>
    <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{sub}</div>
  </div>;
}
function Mini({ label, value, small }) {
  return <div style={{ background:G.input, padding:8, borderRadius:6 }}>
    <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize: small ? 13 : 16, fontWeight:700, marginTop:2 }}>{value}</div>
  </div>;
}

function KpisTab({ toast }) {
  const [kpis, setKpis] = useState(null);
  useEffect(() => { api('/api/agency/kpis').then(setKpis).catch(e=>toast(e.message,'error')); }, []);
  if (!kpis) return <Spinner sz={32}/>;

  return <>
    <div style={{ ...styles.card, padding:14, marginBottom:14, fontSize:13, color:G.textMuted, borderColor:G.purple+'66', background:G.purpleDim }}>
      🔒 <strong style={{ color:G.text }}>Agency-only view.</strong> Sub-accounts only see their own data — never each other's.
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:12, marginBottom:18 }}>
      <Stat label="Active Accounts" value={kpis.activeAccounts} sub={`${kpis.newAccountsLast30} new in 30d`} color={G.cyan}/>
      <Stat label="Total MRR" value={fmtUsd(kpis.totalMrr)} sub={`${fmtUsd(Math.round(kpis.totalMrr*12))} ARR`} color={G.green}/>
      <Stat label="Client Revenue (30d)" value={fmtUsd(kpis.revenueLast30)} sub={`${fmtUsd(kpis.totalRevenueAllTime)} all-time`} color={G.purple}/>
      <Stat label="AI Bookings" value={`${kpis.aiBookedCount}/${kpis.aiConversations}`} sub={`${kpis.aiBookingRate}% rate`} color={G.orange}/>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 10px 0', fontSize:14 }}>💳 Financing this month</h3>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}>
          <span style={{ color:G.textMuted }}>Approved</span>
          <strong>{kpis.financingApproved}</strong>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}>
          <span style={{ color:G.textMuted }}>Total financed</span>
          <strong>{fmtUsd(kpis.financingTotalFinanced)}</strong>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', color:G.green }}>
          <span>Referral fees earned</span>
          <strong>{fmtUsd(kpis.financingFeesEarned)}</strong>
        </div>
      </div>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 10px 0', fontSize:14 }}>⭐ Reviews this month</h3>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}>
          <span style={{ color:G.textMuted }}>Total ratings</span>
          <strong>{kpis.reviewsCount}</strong>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}>
          <span style={{ color:G.textMuted }}>Routed to public Google</span>
          <strong style={{ color:G.green }}>{kpis.reviewsPublic}</strong>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}>
          <span style={{ color:G.textMuted }}>Avg rating</span>
          <strong>★ {kpis.reviewsAvgRating}</strong>
        </div>
      </div>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 10px 0', fontSize:14 }}>🏆 Top Accounts (last 30d revenue)</h3>
        {kpis.topAccounts?.map((a,i) => (
          <div key={a.id} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom: i<kpis.topAccounts.length-1?`1px solid ${G.border}`:'none', fontSize:13 }}>
            <span>{i===0?'🥇':i===1?'🥈':i===2?'🥉':'#'+(i+1)} {a.name}</span>
            <strong style={{ color:G.green }}>{fmtUsd(a.revenue)}</strong>
          </div>
        ))}
        {(!kpis.topAccounts || kpis.topAccounts.length===0) && <div style={{ color:G.textMuted, fontSize:13 }}>No revenue yet.</div>}
      </div>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 10px 0', fontSize:14, color:G.yellow }}>⚠️ Accounts at Risk</h3>
        {kpis.atRiskAccounts?.length === 0 ? <div style={{ color:G.green, fontSize:13 }}>None — every account has activity.</div>
        : kpis.atRiskAccounts?.map(a => (
          <div key={a.id} style={{ padding:'6px 0', borderBottom:`1px solid ${G.border}`, fontSize:13 }}>
            <div><strong>{a.name}</strong> · health {a.health}%</div>
            <div style={{ fontSize:11, color:G.textMuted }}>{a.leads_30d} leads · {a.jobs_30d} jobs in 30d · {fmtUsd(a.mrr)} MRR</div>
          </div>
        ))}
      </div>
    </div>
  </>;
}

function NumbersTab({ toast }) {
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [buyAreaCode, setBuyAreaCode] = useState('813');
  const [buyAccountId, setBuyAccountId] = useState('');

  const load = () => Promise.all([
    api('/api/agency/numbers').then(setData).catch(e => toast(e.message, 'error')),
    api('/api/agency/accounts').then(setAccounts).catch(()=>{}),
  ]);
  useEffect(() => { load(); }, []);

  if (!data) return <Spinner sz={32}/>;
  if (data.error) return <div style={{ ...styles.card, padding:20, borderColor:G.yellow+'66', background:G.yellowLight }}>
    ⚠️ {data.error}. Set <code>TWILIO_ACCOUNT_SID</code> + <code>TWILIO_AUTH_TOKEN</code> on Fly first.
  </div>;

  const assign = async (phoneSid, phoneNumber, accountId) => {
    if (!accountId) return;
    setBusy(phoneSid);
    try { await api('/api/agency/numbers/assign', { method:'POST', body:{ phoneSid, phoneNumber, accountId }}); await load(); toast('Number assigned + webhooks rewired'); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const buy = async () => {
    if (!buyAccountId) return toast('Pick an account to assign the new number to', 'error');
    setBusy('buy');
    try { const r = await api('/api/agency/numbers/buy', { method:'POST', body:{ areaCode: buyAreaCode, accountId: buyAccountId }}); toast(`Bought + assigned ${r.phone}`); await load(); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(null); }
  };

  return <>
    <div style={{ ...styles.card, padding:18, marginBottom:18 }}>
      <strong style={{ fontSize:15 }}>🛒 Buy a new number</strong>
      <div style={{ display:'flex', gap:8, marginTop:10, alignItems:'flex-end' }}>
        <Field label="Area code"><Input value={buyAreaCode} onChange={e=>setBuyAreaCode(e.target.value)} maxLength={3} style={{ width:80 }}/></Field>
        <Field label="Assign to sub-account">
          <Select value={buyAccountId} onChange={e=>setBuyAccountId(e.target.value)} style={{ width:280 }}>
            <option value="">— Pick account —</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <Btn v="primary" onClick={buy} disabled={busy==='buy'}>{busy==='buy'?<Spinner sz={14}/>:'Buy + Assign'}</Btn>
      </div>
      <div style={{ fontSize:11, color:G.textDim, marginTop:6 }}>~$1.15/mo per number, billed to your platform Twilio account.</div>
    </div>

    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead><tr>
          {['Phone Number','Assigned To','Capabilities','Reassign','Bought'].map(h => <th key={h} style={styles.th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {data.numbers.map(n => (
            <tr key={n.sid}>
              <td style={{ ...styles.td, fontFamily:'monospace' }}>{n.phoneNumber}</td>
              <td style={styles.td}>
                {n.assignedTo
                  ? <Badge c={G.green} bg={G.greenLight}>{n.assignedTo.name}</Badge>
                  : <Badge c={G.yellow} bg={G.yellowLight}>Unassigned</Badge>}
              </td>
              <td style={styles.td}>
                {n.smsEnabled && <Badge c={G.cyan} bg={G.cyan+'22'}>SMS</Badge>}
                {n.voiceEnabled && <Badge c={G.purple} bg={G.purpleDim} style={{ marginLeft:4 }}>Voice</Badge>}
              </td>
              <td style={styles.td}>
                <Select disabled={busy===n.sid} value={n.assignedTo?.id || ''} onChange={e=>assign(n.sid, n.phoneNumber, e.target.value)} style={{ width:240 }}>
                  <option value="">— Pick account —</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </Select>
              </td>
              <td style={{ ...styles.td, fontSize:11, color:G.textMuted }}>{new Date(n.dateCreated).toLocaleDateString()}</td>
            </tr>
          ))}
          {data.numbers.length === 0 && <tr><td colSpan={5} style={{ ...styles.td, textAlign:'center', color:G.textMuted, padding:30 }}>
            No numbers yet. Click "Buy + Assign" to provision your first number.
          </td></tr>}
        </tbody>
      </table>
    </div>
  </>;
}

// ─── Coaching Reports (super-admin) ────────────────────────────────────────
// One place to generate + review + send Profit Coach emails for every
// sub-account. Scheduled drafts also land here for review before going out.
function CoachingReportsTab({ toast }) {
  const [accounts, setAccounts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(null); // account id mid-generate
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [cadence, setCadence] = useState('monthly');

  const loadAccounts = () => api('/api/agency/accounts').then(setAccounts).catch(()=>{});
  const loadDrafts = () => api('/api/financials/profit-coach/drafts?all=1').then(setDrafts).catch(()=>{});
  useEffect(() => { loadAccounts(); loadDrafts(); }, []);

  const generate = async (accountId, accountName) => {
    setBusy(accountId);
    try {
      const r = await api('/api/financials/profit-coach/send', { method:'POST', body:{ accountId, cadence, asDraft: true }});
      toast(`✓ Draft created for ${accountName} — ${r.leakCount} leak${r.leakCount===1?'':'s'}, ~$${Math.round(r.totalPotentialSavings||0).toLocaleString()}/yr`);
      loadDrafts();
    } catch(e) { toast(e.message, 'error'); } finally { setBusy(null); }
  };

  const previewDraft = async (draft) => {
    try {
      const r = await fetch(`/api/financials/profit-coach/drafts/${draft.id}/html`, { credentials:'include' });
      const html = await r.text();
      setPreviewHtml(html);
      setPreviewMeta(draft);
    } catch(e) { toast(e.message, 'error'); }
  };

  const sendDraft = async (draft) => {
    if (!confirm(`Send this coaching report to ${draft.sentTo || draft.accountName}?`)) return;
    try {
      await api(`/api/financials/profit-coach/drafts/${draft.id}/send`, { method:'POST' });
      toast(`✓ Sent to ${draft.accountName}`);
      setPreviewHtml(null);
      setPreviewMeta(null);
      loadDrafts();
    } catch(e) { toast(e.message, 'error'); }
  };

  const discardDraft = async (draft) => {
    if (!confirm(`Discard this draft for ${draft.accountName}?`)) return;
    try {
      await api(`/api/financials/profit-coach/drafts/${draft.id}`, { method:'DELETE' });
      toast('Draft discarded');
      loadDrafts();
    } catch(e) { toast(e.message, 'error'); }
  };

  return <>
    <div style={{ ...styles.card, padding:18, marginBottom:14 }}>
      <h3 style={{ margin:'0 0 6px 0' }}>📈 Coaching Reports</h3>
      <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
        Generate a profit-leak coaching draft for any sub-account, review it, then send it to the owner. Scheduled drafts (weekly Monday, monthly 1st) also appear here for your review before going out.
      </div>
      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
        <span style={{ fontSize:13, color:G.textMuted }}>Period:</span>
        <Select value={cadence} onChange={e=>setCadence(e.target.value)} style={{ width:180 }}>
          <option value="weekly">Weekly (last 7 days)</option>
          <option value="monthly">Monthly (last 30 days)</option>
        </Select>
      </div>
    </div>

    {drafts.length > 0 && <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor:G.yellow+'66' }}>
      <h3 style={{ margin:'0 0 12px 0', fontSize:15 }}>📥 Pending Drafts ({drafts.length})</h3>
      <div style={{ display:'grid', gap:8 }}>
        {drafts.map(d => (
          <div key={d.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', background:G.input, borderRadius:8, gap:10, flexWrap:'wrap' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700 }}>{d.accountName || '(unknown)'}</div>
              <div style={{ fontSize:11, color:G.textMuted }}>
                {d.cadence === 'weekly' ? 'Weekly' : 'Monthly'} · {d.report?.leaks?.length || 0} leak{(d.report?.leaks?.length||0)===1?'':'s'} · ~${Math.round(d.report?.totalPotentialSavings||0).toLocaleString()}/yr · {new Date(d.createdAt).toLocaleString()} · → {d.sentTo || '(no email)'}
              </div>
            </div>
            <div style={{ display:'flex', gap:6 }}>
              <Btn sz="xs" onClick={()=>previewDraft(d)}>👁 Preview</Btn>
              <Btn sz="xs" v="primary" onClick={()=>sendDraft(d)}>📧 Send</Btn>
              <Btn sz="xs" v="danger" onClick={()=>discardDraft(d)}>Discard</Btn>
            </div>
          </div>
        ))}
      </div>
    </div>}

    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <div style={{ padding:'12px 16px', borderBottom:`1px solid ${G.border}` }}>
        <h3 style={{ margin:0, fontSize:15 }}>Generate Draft for Account</h3>
      </div>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead>
          <tr style={{ background:G.input }}>
            <th style={styles.th}>Account</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Industry</th>
            <th style={styles.th}>Action</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map(a => (
            <tr key={a.id}>
              <td style={styles.td}>{a.name}</td>
              <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{a.email || '—'}</td>
              <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{a.industry || '—'}</td>
              <td style={styles.td}>
                <Btn sz="xs" v="primary" disabled={busy===a.id || !a.email} onClick={()=>generate(a.id, a.name)}>
                  {busy===a.id ? <Spinner sz={12}/> : '📝 Generate draft'}
                </Btn>
              </td>
            </tr>
          ))}
          {accounts.length === 0 && <tr><td colSpan={4} style={{ ...styles.td, textAlign:'center', color:G.textMuted, padding:30 }}>No sub-accounts yet.</td></tr>}
        </tbody>
      </table>
    </div>

    {previewHtml && <Modal open={true} width={900} onClose={()=>{ setPreviewHtml(null); setPreviewMeta(null); }}>
      <h3 style={{ margin:'0 0 14px 0' }}>Preview — {previewMeta?.accountName||''}</h3>
      <div style={{ maxHeight:'65vh', overflow:'auto', border:`1px solid ${G.cardBorder}`, borderRadius:8 }}>
        {/* sandbox="" fully isolates the preview: no scripts, no forms, no same-origin access. The
            report is static email HTML (no inline scripts), and accountName/ownerName are tenant-set,
            so a payload in a business name must not run in this super_admin session. */}
        <iframe sandbox="" srcDoc={previewHtml} style={{ width:'100%', height:'65vh', border:'none', background:'#fff' }} title="Coaching email preview"/>
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:14 }}>
        <Btn v="danger" onClick={()=>discardDraft(previewMeta)}>Discard</Btn>
        <Btn v="primary" onClick={()=>sendDraft(previewMeta)}>📧 Send to {previewMeta?.sentTo}</Btn>
      </div>
    </Modal>}
  </>;
}

// ─── Margin Coach (super-admin only) ────────────────────────────────────────
// Live per-account margin: MRR + Stripe Connect fees + Creative Credits IN,
// minus voice/SMS/Anthropic + hosting OUT. Shows trend vs previous period
// so Kyle can spot accounts trending unprofitable before they're underwater.
function MarginCoachTab({ toast }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { api(`/api/agency/margin?days=${days}`).then(setData).catch(e=>toast(e.message,'error')); }, [days]);
  if (!data) return <Spinner sz={32}/>;
  const fmtC = (c) => `$${(c/100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const trendIcon = (t) => t==='up' ? '↑' : t==='down' ? '↓' : '·';
  const trendColor = (t) => t==='up' ? G.green : t==='down' ? G.red : G.textMuted;
  return <>
    <div style={{ ...styles.card, padding:18, marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, flexWrap:'wrap', gap:10 }}>
        <h3 style={{ margin:0 }}>💰 Margin Coach</h3>
        <div style={{ display:'flex', gap:6, background:G.input, padding:4, borderRadius:8 }}>
          {[7, 30, 90].map(d => <Btn key={d} sz="xs" v={days===d?'primary':'ghost'} onClick={()=>setDays(d)}>{d}d</Btn>)}
        </div>
      </div>
      <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
        Per-account profit: flat MRR + Stripe Connect fees + Creative Credits IN, minus voice/SMS/Claude/hosting OUT. Voice/SMS/Anthropic are absorbed in the flat rate — image/video are billed separately as credit packs.
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:10 }}>
        <KpiTile label="Total Revenue In" value={fmtC(data.totals.inCents)} color={G.cyan}/>
        <KpiTile label="Total Costs Out" value={fmtC(data.totals.outCents)} color={G.orange}/>
        <KpiTile label="Net Margin" value={fmtC(data.totals.marginCents)} color={data.totals.marginCents>=0?G.green:G.red}/>
        <KpiTile label="Margin %" value={`${data.totals.marginPct}%`} color={data.totals.marginPct>=30?G.green:data.totals.marginPct>=0?G.yellow:G.red}/>
      </div>
    </div>

    <div style={{ ...styles.card, padding:0, overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', minWidth:880 }}>
        <thead><tr style={{ background:G.input }}>
          {['Account','Revenue In','Costs Out','Margin','Margin %','Voice (min)','SMS','Trend'].map(h => <th key={h} style={{ ...styles.th, whiteSpace:'nowrap' }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {data.accounts.map(a => (
            <tr key={a.accountId} style={{ background: a.marginCents<0 ? G.red+'14' : 'transparent' }}>
              <td style={{ ...styles.td, fontWeight:600 }}>{a.accountName}</td>
              <td style={{ ...styles.td, color:G.cyan, whiteSpace:'nowrap' }}>{fmtC(a.in.totalCents)}</td>
              <td style={{ ...styles.td, color:G.orange, whiteSpace:'nowrap' }}>{fmtC(a.out.totalCents)}</td>
              <td style={{ ...styles.td, fontWeight:700, color:a.marginCents>=0?G.green:G.red, whiteSpace:'nowrap' }}>{fmtC(a.marginCents)}</td>
              <td style={{ ...styles.td, color:a.marginPct>=30?G.green:a.marginPct>=0?G.yellow:G.red, whiteSpace:'nowrap' }}>{a.marginPct}%</td>
              <td style={{ ...styles.td, fontSize:12, color:G.textMuted, whiteSpace:'nowrap' }}>{a.out.voiceMinutes} min · {fmtC(a.out.voiceCents)}</td>
              <td style={{ ...styles.td, fontSize:12, color:G.textMuted, whiteSpace:'nowrap' }}>{a.out.smsCount} · {fmtC(a.out.smsCents)}</td>
              <td style={{ ...styles.td, fontWeight:700, color:trendColor(a.trend), whiteSpace:'nowrap' }}>
                {trendIcon(a.trend)} {a.deltaPct !== null ? `${a.deltaPct>=0?'+':''}${a.deltaPct}%` : '—'}
              </td>
            </tr>
          ))}
          {data.accounts.length === 0 && <tr><td colSpan={8} style={{ ...styles.td, textAlign:'center', color:G.textMuted, padding:30 }}>No active accounts yet.</td></tr>}
        </tbody>
      </table>
    </div>
  </>;
}

function KpiTile({ label, value, color }) {
  return <div style={{ background:G.input, padding:14, borderRadius:8 }}>
    <div style={{ fontSize:11, color:G.textMuted, fontWeight:600, textTransform:'uppercase' }}>{label}</div>
    <div style={{ fontSize:22, fontWeight:800, color, marginTop:2 }}>{value}</div>
  </div>;
}
